import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { FolderOpen, FileText, Calendar, Plus, ChevronRight, ChevronDown, Coins, Loader, Trash2, RotateCcw, Pencil, Inbox, CheckCircle, Flag, Search, Archive, ArchiveRestore, ArrowUpDown, HardDrive, Sparkles, Check, Star, Target, AlertTriangle, Clock, GripVertical, FileUp, MessageCircle, Undo2, Redo2, ExternalLink } from "lucide-react";
import PageHeader from "./PageHeader";
import { generationEta } from "./genEta";
import { formatCost, formatEur } from "./cost";
import { FarbtypDots } from "./farbtyp";
import { navigate } from "./router";

// Rendert einfachen Formatierungs-Text (**fett**, __unterstrichen__, Zeilen-
// umbrüche) als ECHTE React-Elemente statt als HTML-String. Dadurch wird kein
// HTML interpretiert — beliebiger Text (auch aus KI/Nutzereingaben) kann so
// weder <script> noch <img onerror=…> einschleusen (XSS-Schutz).
function renderFormattedText(text) {
  const src = String(text ?? "");
  const out = [];
  let key = 0;
  const pushPlain = (str) => {
    const parts = str.split("\n");
    parts.forEach((p, i) => {
      if (i > 0) out.push(<br key={`b${key++}`} />);
      if (p) out.push(p);
    });
  };
  const regex = /\*\*(.+?)\*\*|__(.+?)__/gs;
  let last = 0;
  let m;
  while ((m = regex.exec(src)) !== null) {
    if (m.index > last) pushPlain(src.slice(last, m.index));
    if (m[1] !== undefined) out.push(<strong key={`s${key++}`}>{m[1]}</strong>);
    else out.push(<u key={`u${key++}`}>{m[2]}</u>);
    last = regex.lastIndex;
  }
  if (last < src.length) pushPlain(src.slice(last));
  return out;
}

function splitSentences(text) {
  return text
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function formatDate(dateStr) {
  if (!dateStr) return "–";
  const d = new Date(dateStr);
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}


const FARBTYP_OPTIONS = [
  { value: "rot-gelb", label: "Rot-Gelb" },
  { value: "blau-gruen", label: "Blau-Grün" },
  { value: "blau-rot", label: "Blau-Rot" },
  { value: "gruen-blau", label: "Grün-Blau" },
  { value: "rot-blau", label: "Rot-Blau" },
  { value: "gelb-gruen", label: "Gelb-Grün" },
  { value: "gelb-rot", label: "Gelb-Rot" },
  { value: "gruen-gelb", label: "Grün-Gelb" },
];

const BENEFIT_TYPE_LABELS = {
  rot: "Rot",
  gelb: "Gelb",
  gruen: "Grün",
  blau: "Blau",
  allgemein: "Allgemein",
  zusaetzlich: "Zusätzlich",
};

const STATUS_OPTIONS = [
  { value: "entwurf", label: "Entwurf" },
  { value: "in_bearbeitung", label: "In Bearbeitung" },
  { value: "warten_partner", label: "Warten auf Partner" },
  { value: "abgeschlossen", label: "Abgeschlossen" },
];
const STATUS_BY_VALUE = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.value, s.label]));
function statusLabel(v) { return STATUS_BY_VALUE[v] || "Entwurf"; }

// --- Build editable blocks from section structure ---

function getSectionItemLabel(sectionName) {
  const map = {
    "Aufgabenbeschreibung": "Aufgaben",
    "Kandidatenprofil": "Anforderungen",
    "Disqualifikation": "Ausschlusskriterien",
    "Angebot": "Benefits",
  };
  return map[sectionName] || null;
}

function getBlocks(section) {
  const isStructured = Boolean(section.headline);
  if (!isStructured) {
    // Legacy format: split content into sentences
    return splitSentences(section.content || "").map((s, i) => ({
      type: "sentence", text: s, key: `s-${i}`, label: null,
    }));
  }

  const blocks = [];
  if (section.headline) {
    if (section.name === "Intro") {
      // Split headline into Einstiegsfrage (line 1) and Überschrift (line 2)
      const headlineLines = section.headline.split("\n").filter(l => l.trim());
      if (headlineLines.length >= 2) {
        blocks.push({ type: "headline-part", text: headlineLines[0].trim(), key: "headline-0", partIndex: 0, label: null });
        blocks.push({ type: "headline-part", text: headlineLines.slice(1).join("\n").trim(), key: "headline-1", partIndex: 1, label: null });
      } else {
        blocks.push({ type: "headline", text: section.headline, key: "headline", label: null });
      }
    } else {
      blocks.push({ type: "headline", text: section.headline, key: "headline", label: null });
    }
  }
  if (section.body) {
    const paragraphs = section.body.split("\n\n").filter(p => p.trim());
    paragraphs.forEach((p, i) => {
      // Intro body-0 = the 3 Fragen block: split into individual draggable questions
      if (section.name === "Intro" && i === 0) {
        const raw = p.trim().replace(/^\*\*/, "").replace(/\*\*$/, "");
        const questions = raw.split("\n").map(q => q.trim()).filter(Boolean);
        questions.forEach((q, qi) => {
          blocks.push({ type: "question", text: q, key: `question-${qi}`, index: qi, draggable: true, dragGroup: "questions", label: null });
        });
      } else {
        blocks.push({ type: "body", text: p.trim(), key: `body-${i}`, index: i, label: null });
      }
    });
  }
  if (section.einleitung) {
    blocks.push({ type: "einleitung", text: section.einleitung, key: "einleitung", label: null });
  }
  if (section.items) {
    const groupLabel = getSectionItemLabel(section.name);
    section.items.forEach((item, i) => {
      blocks.push({ type: "item", text: item, key: `item-${i}`, index: i, itemNum: i + 1, draggable: true, dragGroup: "items", label: i === 0 ? groupLabel : null });
    });
  }
  return blocks;
}

function applyBlockUpdate(section, blockKey, newText) {
  const updated = { ...section };
  if (blockKey === "headline") {
    updated.headline = newText;
  } else if (blockKey === "headline-0" || blockKey === "headline-1") {
    const headlineLines = (updated.headline || "").split("\n").filter(l => l.trim());
    if (blockKey === "headline-0") {
      headlineLines[0] = newText;
    } else {
      headlineLines[1] = newText;
    }
    updated.headline = headlineLines.join("\n");
  } else if (blockKey === "einleitung") {
    updated.einleitung = newText;
  } else if (blockKey.startsWith("question-")) {
    // Edit a single question within the Intro 3-Fragen block
    const idx = parseInt(blockKey.split("-")[1]);
    const paragraphs = (updated.body || "").split("\n\n");
    const raw = paragraphs[0].trim().replace(/^\*\*/, "").replace(/\*\*$/, "");
    const questions = raw.split("\n").map(q => q.trim()).filter(Boolean);
    questions[idx] = newText;
    paragraphs[0] = "**" + questions.join("\n") + "**";
    updated.body = paragraphs.join("\n\n");
  } else if (blockKey.startsWith("body-")) {
    const idx = parseInt(blockKey.split("-")[1]);
    const paragraphs = (updated.body || "").split("\n\n");
    paragraphs[idx] = newText;
    updated.body = paragraphs.join("\n\n");
  } else if (blockKey.startsWith("item-")) {
    const idx = parseInt(blockKey.split("-")[1]);
    updated.items = [...(updated.items || [])];
    updated.items[idx] = newText;
  } else {
    return newText;
  }
  updated.content = [updated.headline, updated.body, updated.einleitung, ...(updated.items || [])].filter(Boolean).join("\n\n");
  return updated;
}

function applyReorder(section, dragGroup, fromIndex, toIndex) {
  const updated = { ...section };
  if (dragGroup === "items") {
    updated.items = [...(updated.items || [])];
    const [moved] = updated.items.splice(fromIndex, 1);
    updated.items.splice(toIndex, 0, moved);
  } else if (dragGroup === "questions") {
    const paragraphs = (updated.body || "").split("\n\n");
    const raw = paragraphs[0].trim().replace(/^\*\*/, "").replace(/\*\*$/, "");
    const questions = raw.split("\n").map(q => q.trim()).filter(Boolean);
    const [moved] = questions.splice(fromIndex, 1);
    questions.splice(toIndex, 0, moved);
    paragraphs[0] = "**" + questions.join("\n") + "**";
    updated.body = paragraphs.join("\n\n");
  }
  updated.content = [updated.headline, updated.body, updated.einleitung, ...(updated.items || [])].filter(Boolean).join("\n\n");
  return updated;
}

// --- Section block with structured rendering ---

function SectionBlock({ section, sectionIndex, onUpdateSection, reviewed, onToggleReview, anzeige, forceExpanded }) {
  const [expanded, setExpanded] = useState(!!forceExpanded);
  const [editingKey, setEditingKey] = useState(null);
  const [editText, setEditText] = useState("");
  const [aiLoading, setAiLoading] = useState(null);
  const [aiPromptKey, setAiPromptKey] = useState(null);
  const [aiPromptText, setAiPromptText] = useState("");
  const [feedbackKey, setFeedbackKey] = useState(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackAlsoRefine, setFeedbackAlsoRefine] = useState(true);
  const [feedbackLoading, setFeedbackLoading] = useState(null);
  const [feedbackDone, setFeedbackDone] = useState(null); // block.key, kurz nach Erfolg
  const [dragOver, setDragOver] = useState(null);
  const dragItem = useRef(null);

  const blocks = getBlocks(section);
  const isStructured = Boolean(section.headline);

  const removeItemAt = (idx) => {
    const items = [...(section.items || [])];
    items.splice(idx, 1);
    const updated = { ...section, items };
    updated.content = [updated.headline, updated.body, updated.einleitung, ...items].filter(Boolean).join("\n\n");
    onUpdateSection(sectionIndex, updated);
  };

  const handleEditSave = (block) => {
    // Leeren Punkt beim Speichern verwerfen, statt eine leere Zeile zu hinterlassen.
    if (block.type === "item" && editText.trim() === "") {
      removeItemAt(block.index);
      setEditingKey(null);
      setEditText("");
      return;
    }
    const result = applyBlockUpdate(section, block.key, editText);
    onUpdateSection(sectionIndex, result);
    setEditingKey(null);
    setEditText("");
  };

  const handleCancelEdit = (block) => {
    // Abbrechen eines frisch hinzugefügten, leeren Punkts → wieder entfernen.
    if (block && block.type === "item" && ((section.items || [])[block.index] || "").trim() === "") {
      removeItemAt(block.index);
    }
    setEditingKey(null);
    setEditText("");
  };

  const handleDeleteItem = (block) => {
    const text = (block.text || "").trim();
    if (text && !window.confirm("Diesen Punkt wirklich löschen?")) return;
    removeItemAt(block.index);
  };

  const handleAddItem = () => {
    const items = [...(section.items || []), ""];
    const updated = { ...section, items };
    updated.content = [updated.headline, updated.body, updated.einleitung, ...items].filter(Boolean).join("\n\n");
    onUpdateSection(sectionIndex, updated);
    setEditingKey(`item-${items.length - 1}`);
    setEditText("");
    if (!expanded) setExpanded(true);
  };

  const handleAiRefine = async (block, instruction) => {
    setAiLoading(block.key);
    setAiPromptKey(null);
    setAiPromptText("");
    try {
      const res = await fetch("/api/refine-block", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          text: block.text,
          blockLabel: block.label || block.type,
          sectionName: section.name,
          instruction: instruction || "",
          farbtyp: anzeige?.farbtyp,
          unternehmen: anzeige?.unternehmen,
          stellentitel: anzeige?.stellentitel,
          standort: anzeige?.standort,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      const result = applyBlockUpdate(section, block.key, data.refined);
      onUpdateSection(sectionIndex, result);
    } catch (err) {
      console.error("AI refine error:", err);
    } finally {
      setAiLoading(null);
    }
  };

  // Feedback zu einem Punkt → Lern-Agent destilliert eine Regel (Vorschlag, wartet
  // auf Freigabe unter "Gelernte Regeln"). Optional wird der Punkt sofort verbessert.
  const handleFeedbackLearn = async (block) => {
    const fb = feedbackText.trim();
    if (!fb) return;
    setFeedbackLoading(block.key);
    setFeedbackKey(null);
    try {
      const res = await fetch("/api/redaktions-regeln/lernen", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          original: block.text,
          feedback: fb,
          section: section.name,
          anzeigeId: anzeige?.id,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Fehler");
      // Optional: denselben Punkt direkt anhand des Feedbacks neu formulieren.
      if (feedbackAlsoRefine) {
        await handleAiRefine(block, fb);
      }
      setFeedbackDone(block.key);
      setTimeout(() => setFeedbackDone((k) => (k === block.key ? null : k)), 4000);
    } catch (err) {
      console.error("Feedback lernen error:", err);
      alert("Feedback konnte nicht verarbeitet werden: " + err.message);
    } finally {
      setFeedbackLoading(null);
      setFeedbackText("");
    }
  };

  // Drag & drop handlers
  const handleDragStart = (e, block) => {
    dragItem.current = block;
    e.dataTransfer.effectAllowed = "move";
    e.currentTarget.classList.add("block-dragging");
  };

  const handleDragEnd = (e) => {
    e.currentTarget.classList.remove("block-dragging");
    dragItem.current = null;
    setDragOver(null);
  };

  const handleDragOver = (e, block) => {
    e.preventDefault();
    if (!dragItem.current || dragItem.current.dragGroup !== block.dragGroup) return;
    e.dataTransfer.dropEffect = "move";
    setDragOver(block.key);
  };

  const handleDrop = (e, block) => {
    e.preventDefault();
    setDragOver(null);
    if (!dragItem.current || dragItem.current.dragGroup !== block.dragGroup) return;
    const fromIndex = dragItem.current.index;
    const toIndex = block.index;
    if (fromIndex === toIndex) return;
    const result = applyReorder(section, block.dragGroup, fromIndex, toIndex);
    onUpdateSection(sectionIndex, result);
    dragItem.current = null;
  };

  const renderPreview = () => {
    if (!isStructured) {
      return <p className="section-preview-text">{(section.content || "").slice(0, 200)}...</p>;
    }
    return (
      <div className="section-preview-text">
        <strong>{section.headline}</strong>
      </div>
    );
  };

  return (
    <div className={`section-block card ${reviewed ? "section-reviewed" : ""}`}>
      <div className="section-header" onClick={() => setExpanded(!expanded)}>
        <span className="section-chevron">{expanded ? "▾" : "▸"}</span>
        <h3>{section.name}</h3>
      </div>
      {!expanded && <div className="section-preview" onClick={() => setExpanded(true)}>{renderPreview()}</div>}
      {expanded && (
        <div className="section-body">
          {blocks.map((block) => (
            <div
              key={block.key}
              className={`block-wrapper ${dragOver === block.key ? "block-drag-over" : ""}`}
              draggable={block.draggable && editingKey !== block.key ? true : undefined}
              onDragStart={block.draggable ? (e) => handleDragStart(e, block) : undefined}
              onDragEnd={block.draggable ? handleDragEnd : undefined}
              onDragOver={block.draggable ? (e) => handleDragOver(e, block) : undefined}
              onDragLeave={block.draggable ? () => setDragOver(null) : undefined}
              onDrop={block.draggable ? (e) => handleDrop(e, block) : undefined}
            >
              {block.label && <div className="block-label">{block.label}</div>}
              <div className={`block-row block-type-${block.type}`}>
                {editingKey === block.key ? (
                  <div className="edit-inline">
                    <textarea
                      className="edit-textarea"
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      rows={Math.max(3, editText.split("\n").length + 1)}
                      autoFocus
                    />
                    <div className="refine-actions">
                      <button className="btn-refine-go" onClick={() => handleEditSave(block)}>Speichern</button>
                      <button className="btn-refine-cancel" onClick={() => handleCancelEdit(block)}>Abbrechen</button>
                    </div>
                  </div>
                ) : (
                  <div className="block-content-row">
                    {block.draggable && (
                      <span className="block-drag-handle"><GripVertical size={14} /></span>
                    )}
                    <div className="block-content-main">
                      <div className="block-text">
                        {block.type === "item" && <span className="block-item-num">{block.itemNum}.</span>}
                        <span>{renderFormattedText(block.text)}</span>
                      </div>
                    </div>
                    <div className="block-icons">
                      <button
                        className="block-icon-btn"
                        data-tooltip="Bearbeiten"
                        onClick={() => { setEditingKey(block.key); setEditText(block.text); }}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        className={`block-icon-btn block-icon-ai ${aiLoading === block.key ? "block-icon-loading" : ""}`}
                        data-tooltip="Mit KI überarbeiten"
                        disabled={aiLoading !== null}
                        onClick={() => { setAiPromptKey(aiPromptKey === block.key ? null : block.key); setAiPromptText(""); }}
                      >
                        {aiLoading === block.key ? <Loader size={14} className="spin" /> : <Sparkles size={14} />}
                      </button>
                      <button
                        className={`block-icon-btn block-icon-feedback ${feedbackDone === block.key ? "block-icon-done" : ""}`}
                        data-tooltip="Feedback geben (KI lernt daraus)"
                        disabled={feedbackLoading !== null}
                        onClick={() => { setFeedbackKey(feedbackKey === block.key ? null : block.key); setFeedbackText(""); }}
                      >
                        {feedbackLoading === block.key ? <Loader size={14} className="spin" />
                          : feedbackDone === block.key ? <Check size={14} />
                          : <MessageCircle size={14} />}
                      </button>
                      {block.type === "item" && (
                        <button
                          className="block-icon-btn block-icon-del"
                          data-tooltip="Punkt löschen"
                          onClick={() => handleDeleteItem(block)}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                )}
                {aiPromptKey === block.key && editingKey !== block.key && (
                  <div className="ai-prompt-inline">
                    <div className="ai-prompt-header">
                      <Sparkles size={14} />
                      <span>KI-Anweisung</span>
                    </div>
                    <input
                      className="ai-prompt-input"
                      type="text"
                      placeholder="z.B. Mach den Text emotionaler / Kürze den Absatz / Mehr auf Teamarbeit eingehen..."
                      value={aiPromptText}
                      onChange={(e) => setAiPromptText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && aiPromptText.trim()) handleAiRefine(block, aiPromptText.trim()); }}
                      autoFocus
                    />
                    <div className="ai-prompt-actions">
                      <button
                        className="btn-ai-go"
                        disabled={!aiPromptText.trim()}
                        onClick={() => handleAiRefine(block, aiPromptText.trim())}
                      >
                        <Sparkles size={13} /> Überarbeiten
                      </button>
                      <button className="btn-ai-auto" onClick={() => handleAiRefine(block, "")}>
                        Automatisch verbessern
                      </button>
                      <button className="btn-ai-cancel" onClick={() => { setAiPromptKey(null); setAiPromptText(""); }}>
                        Abbrechen
                      </button>
                    </div>
                  </div>
                )}
                {feedbackKey === block.key && editingKey !== block.key && (
                  <div className="ai-prompt-inline feedback-inline">
                    <div className="ai-prompt-header">
                      <MessageCircle size={14} />
                      <span>Feedback geben – die KI lernt daraus für künftige Anzeigen</span>
                    </div>
                    <textarea
                      className="ai-prompt-input feedback-textarea"
                      rows={3}
                      placeholder="Was soll an diesem Punkt besser werden? In eigenen Worten, z.B. 'klingt zu sehr nach Handbuch – beschreib lieber, woran man am Ende des Tages sieht, dass es gut lief'..."
                      value={feedbackText}
                      onChange={(e) => setFeedbackText(e.target.value)}
                      autoFocus
                    />
                    <label className="feedback-checkbox">
                      <input
                        type="checkbox"
                        checked={feedbackAlsoRefine}
                        onChange={(e) => setFeedbackAlsoRefine(e.target.checked)}
                      />
                      <span>Diesen Punkt gleich anhand des Feedbacks verbessern</span>
                    </label>
                    <div className="ai-prompt-actions">
                      <button
                        className="btn-ai-go"
                        disabled={!feedbackText.trim()}
                        onClick={() => handleFeedbackLearn(block)}
                      >
                        <MessageCircle size={13} /> Feedback senden &amp; lernen
                      </button>
                      <button className="btn-ai-cancel" onClick={() => { setFeedbackKey(null); setFeedbackText(""); }}>
                        Abbrechen
                      </button>
                    </div>
                  </div>
                )}
                {feedbackDone === block.key && feedbackKey !== block.key && (
                  <div className="feedback-confirm">
                    <Check size={13} /> Regel-Vorschlag gespeichert – unter <strong>KI-Optimierungen</strong> freigeben, dann wirkt er bei jeder neuen Anzeige.
                  </div>
                )}
              </div>
            </div>
          ))}
          {Array.isArray(section.items) && (
            <button className="section-add-item" onClick={handleAddItem}>
              <Plus size={14} /> Punkt hinzufügen
            </button>
          )}
        </div>
      )}
    </div>
  );
}


// --- Jotform Input Panel (left side) ---

function parseAufgabenBlocks(text) {
  if (!text) return [];
  // Split by "**Aufgabe" blocks
  const blocks = text.split(/\*\*Aufgabe/).filter((b) => b.trim());
  return blocks.map((block, idx) => {
    const clean = block.replace(/\*\*/g, "").trim();
    // Format: "(30% der Arbeitszeit): <Beschreibung – ggf. mehrzeilig/mehrere Absätze>
    //          Arbeitsweise: … Kommunikation: … Persönlichkeitstyp: …"
    const pctMatch = clean.match(/\((\d+)\s*%/);
    const pct = pctMatch ? pctMatch[1] : null;
    // Den "(…%...):"-Präfix entfernen (nur am Anfang).
    let rest = clean.replace(/^\([^)]*\)\s*:?\s*/, "");
    // Den Metadaten-Block ab "Arbeitsweise:" abtrennen — alles davor ist die
    // VOLLSTÄNDIGE Beschreibung (inkl. Zeilenumbrüchen/Absätzen).
    let description = rest;
    let metaPart = "";
    const metaIdx = rest.search(/Arbeitsweise\s*:/);
    if (metaIdx >= 0) {
      description = rest.slice(0, metaIdx).trim();
      metaPart = rest.slice(metaIdx).trim();
    } else {
      description = rest.trim();
    }
    // Arbeitsweise / Kommunikation / Persönlichkeitstyp aus der Metazeile lösen.
    const tags = [];
    const meta = metaPart.match(/Arbeitsweise\s*:\s*([\s\S]*?)\s*Kommunikation\s*:\s*([\s\S]*?)\s*Persönlichkeitstyp\s*:\s*([\s\S]*?)\s*$/);
    if (meta) {
      [["Arbeitsweise", meta[1]], ["Kommunikation", meta[2]], ["Persönlichkeitstyp", meta[3]]].forEach(([label, value]) => {
        const v = (value || "").replace(/\.\s*$/, "").trim();
        if (v) tags.push({ label, value: v });
      });
    } else if (metaPart) {
      const v = metaPart.replace(/^Arbeitsweise\s*:\s*/, "").replace(/\.\s*$/, "").trim();
      if (v) tags.push({ label: "Details", value: v });
    }
    return { num: idx + 1, pct, description, tags };
  });
}

function JotformInputPanel({ inputData, anzeige }) {
  const [openSections, setOpenSections] = useState({});
  if (!inputData) {
    return (
      <div className="jotform-panel card">
        <h4 className="jotform-panel-title">Jotform-Eingaben</h4>
        <p className="empty-state">Keine Eingabedaten vorhanden.</p>
      </div>
    );
  }

  const toggle = (key) => setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  // Parse Aufgaben into structured blocks
  const aufgabenBlocks = parseAufgabenBlocks(inputData.aufgaben);

  // Extract Arbeitszeit from Zusatzinfos
  const arbeitszeit = (inputData.zusatzinfos || "").replace(/^Arbeitszeit:\s*/i, "").trim();

  // Build anforderungen sub-sections (new: separate fields for Stärken, Hard Skills, Schwächen)
  // Fallback: parse from combined anforderungen string for older records
  let staerken = inputData.staerken || "";
  let hardSkills = inputData.hardSkills || "";
  let schwaechen = inputData.schwaechen || "";
  if (!staerken && !hardSkills && !schwaechen && inputData.anforderungen) {
    const anf = inputData.anforderungen;
    const stMatch = anf.match(/Stärken:\s*([\s\S]*?)(?=\n\nHard Skills:|$)/);
    const hsMatch = anf.match(/Hard Skills:\s*([\s\S]*?)(?=\n\nAkzeptable Schwächen:|$)/);
    const swMatch = anf.match(/Akzeptable Schwächen:\s*([\s\S]*?)$/);
    if (stMatch) staerken = stMatch[1].trim();
    if (hsMatch) hardSkills = hsMatch[1].trim();
    if (swMatch) schwaechen = swMatch[1].trim();
  }

  // Benefits grouped by personality type (if available)
  const bbt = inputData.benefitsByType || {};
  const hasBenefitsByType = Object.values(bbt).some(Boolean);

  const sections = [
    { key: "aufgaben", label: "Aufgaben", icon: FileText, type: "aufgaben", data: aufgabenBlocks },
    { key: "staerken", label: "Stärken", icon: Star, type: "text", data: staerken },
    { key: "hardskills", label: "Hard Skills", icon: Target, type: "text", data: hardSkills },
    { key: "schwaechen", label: "Schwächen", icon: AlertTriangle, type: "text", data: schwaechen },
    ...(hasBenefitsByType
      ? [{ key: "benefits", label: "Benefits", icon: Coins, type: "benefitsByType", data: bbt }]
      : [{ key: "benefits", label: "Benefits", icon: Coins, type: "text", data: inputData.benefits }]
    ),
    { key: "disqualifikation", label: "Disqualifikation", icon: Trash2, type: "text", data: inputData.disqualifikation },
  ].filter((s) => {
    if (s.type === "aufgaben") return s.data.length > 0;
    if (s.type === "benefitsByType") return Object.values(s.data).some(Boolean);
    return Boolean(s.data);
  });

  return (
    <div className="jotform-panel card">
      <h4 className="jotform-panel-title">Jotform-Eingaben</h4>

      {/* Always-visible top fields */}
      <div className="jf-top-fields">
        {anzeige?.created_at && (
          <div className="jf-top-field jf-top-field-full">
            <span className="jf-top-label">Eingegangen am</span>
            <span className="jf-top-value">{formatDate(anzeige.created_at)} um {new Date(anzeige.created_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })} Uhr</span>
          </div>
        )}
        {anzeige?.standort && (
          <div className="jf-top-field">
            <span className="jf-top-label">Standort</span>
            <span className="jf-top-value">{anzeige.standort}</span>
          </div>
        )}
        {arbeitszeit && (
          <div className="jf-top-field">
            <span className="jf-top-label">Arbeitszeit</span>
            <span className="jf-top-value">{arbeitszeit}</span>
          </div>
        )}
        {anzeige?.farbtyp && (
          <div className="jf-top-field">
            <span className="jf-top-label">Farbtyp</span>
            <span className="jf-top-value jf-farbtyp">{anzeige.farbtyp}</span>
          </div>
        )}
      </div>

      <div className="jotform-sections">
        {sections.map((s) => {
          const Icon = s.icon;
          const isOpen = openSections[s.key];
          return (
            <div key={s.key} className={`jf-section ${isOpen ? "jf-section-open" : ""}`}>
              <div className="jf-section-header" onClick={() => toggle(s.key)}>
                <div className="jf-section-left">
                  <Icon size={14} />
                  <span className="jf-section-label">{s.label}</span>
                </div>
                {s.type === "aufgaben" && <span className="jf-section-count">{s.data.length}</span>}
                <span className="jf-section-chevron">{isOpen ? "▾" : "▸"}</span>
              </div>
              {isOpen && s.type === "aufgaben" && (
                <div className="jf-aufgaben">
                  {s.data.map((block, i) => (
                    <div key={i} className="jf-aufgabe-card">
                      <div className="jf-aufgabe-header">
                        <span className="jf-aufgabe-title">Aufgabe {block.num}</span>
                        {block.pct && <span className="jf-aufgabe-pct">{block.pct}%</span>}
                      </div>
                      <div className="jf-aufgabe-desc">{block.description}</div>
                      {block.tags.length > 0 && (
                        <div className="jf-aufgabe-meta">
                          {block.tags.map((tag, j) => (
                            <span key={j} className="jf-aufgabe-meta-item">
                              <span className="jf-aufgabe-meta-label">{tag.label}:</span> {tag.value}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {isOpen && s.type === "text" && (
                <div className="jf-text-block">{s.data}</div>
              )}
              {isOpen && s.type === "benefitsByType" && (
                <div className="jf-benefits-by-type">
                  {Object.entries(BENEFIT_TYPE_LABELS).filter(([key]) => s.data[key]).map(([key, label]) => (
                    <div key={key} className={`benefit-type-group benefit-type-${key}`}>
                      <div className="benefit-type-label">{label}</div>
                      <div className="benefit-type-text">{s.data[key]}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --- Stellenanzeige detail view ---

// Zerlegt ein mehrzeiliges Eingabefeld in einzelne, abhakbare Punkte.
function splitInputPoints(text) {
  if (!text) return [];
  return String(text)
    .split(/\n+/)
    .map((l) => l.replace(/^\s*[-•*]\s*/, "").trim())
    .filter(Boolean);
}

function buildInputForSection(sectionName, inputData, anzeige) {
  if (!inputData) return [];
  switch (sectionName) {
    case "Intro":
      // Stammdaten – nur zur Info, nicht abhakbar (noCheck).
      return [
        anzeige?.stellentitel && { label: "Stellentitel", value: anzeige.stellentitel, noCheck: true },
        anzeige?.standort && { label: "Standort", value: anzeige.standort, noCheck: true },
        anzeige?.farbtyp && { label: "Farbtyp", value: anzeige.farbtyp, noCheck: true },
      ].filter(Boolean);
    case "Aufgabenbeschreibung": {
      const blocks = parseAufgabenBlocks(inputData.aufgaben);
      return blocks.map((b) => ({
        label: `Aufgabe ${b.num}${b.pct ? ` · ${b.pct}%` : ""}`,
        value: b.description,
        meta: b.tags, // [{label, value}] – beschriftete Reihe (Arbeitsweise/Kommunikation/Persönlichkeitstyp)
      }));
    }
    case "Kandidatenprofil": {
      let staerken = inputData.staerken || "";
      let hardSkills = inputData.hardSkills || "";
      let schwaechen = inputData.schwaechen || "";
      if (!staerken && !hardSkills && !schwaechen && inputData.anforderungen) {
        const anf = inputData.anforderungen;
        const stMatch = anf.match(/Stärken:\s*([\s\S]*?)(?=\n\nHard Skills:|$)/);
        const hsMatch = anf.match(/Hard Skills:\s*([\s\S]*?)(?=\n\nAkzeptable Schwächen:|$)/);
        const swMatch = anf.match(/Akzeptable Schwächen:\s*([\s\S]*?)$/);
        if (stMatch) staerken = stMatch[1].trim();
        if (hsMatch) hardSkills = hsMatch[1].trim();
        if (swMatch) schwaechen = swMatch[1].trim();
      }
      const profil = [];
      [["Stärken", staerken], ["Hard Skills", hardSkills], ["Akzeptable Schwächen", schwaechen]].forEach(([group, val]) => {
        splitInputPoints(val).forEach((p) => profil.push({ group, value: p }));
      });
      return profil;
    }
    case "Disqualifikation":
      return splitInputPoints(inputData.disqualifikation).map((p) => ({ value: p }));
    case "Angebot": {
      const bbt = inputData.benefitsByType || {};
      if (Object.values(bbt).some(Boolean)) {
        const out = [];
        Object.entries(BENEFIT_TYPE_LABELS)
          .filter(([k]) => bbt[k])
          .forEach(([k, l]) => splitInputPoints(bbt[k]).forEach((p) => out.push({ group: l, value: p })));
        return out;
      }
      return splitInputPoints(inputData.benefits).map((p) => ({ value: p }));
    }
    default:
      return [];
  }
}

function sectionToPlainText(section) {
  const parts = [];
  if (section.headline) parts.push(section.headline);
  if (section.body) parts.push(section.body);
  if (section.einleitung) parts.push(section.einleitung);
  if (section.items?.length) parts.push(section.items.map((it) => "• " + it).join("\n"));
  if (!section.headline && !section.body && !section.einleitung && section.content) parts.push(section.content);
  return parts.join("\n\n");
}

function ComparisonTable({ sections, inputData, anzeige, onUpdateSection, reviewFlags, onToggleReview }) {
  const [checks, setChecks] = useState(anzeige?.input_checks || {});
  useEffect(() => { setChecks(anzeige?.input_checks || {}); }, [anzeige?.id]);

  const persistChecks = useCallback((next) => {
    if (!anzeige?.id) return;
    fetch(`/api/stellenanzeige/${anzeige.id}/input-checks`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ checks: next }),
    }).catch(() => {});
  }, [anzeige?.id]);

  const toggleCheck = useCallback((key) => {
    setChecks((prev) => {
      const next = { ...prev };
      if (next[key]) delete next[key]; else next[key] = true;
      persistChecks(next);
      return next;
    });
  }, [persistChecks]);

  // Abdeckungs-Zähler über alle abhakbaren Eingabe-Punkte (Stammdaten/Intro zählen nicht).
  let totalPoints = 0, checkedPoints = 0;
  sections.forEach((s) => {
    buildInputForSection(s.name, inputData, anzeige).forEach((inp, j) => {
      if (inp.noCheck) return;
      totalPoints++;
      if (checks[`${s.name}#${j}`]) checkedPoints++;
    });
  });

  return (
    <div className="compare-table">
      <div className="compare-header">
        <div className="compare-header-cell">
          {anzeige?.source === "uneq" ? "Eingaben" : "Jotform-Eingaben"}
          {totalPoints > 0 && (
            <span className={"compare-coverage" + (checkedPoints === totalPoints ? " is-complete" : "")}>
              {checkedPoints}/{totalPoints} abgehakt
            </span>
          )}
        </div>
        <div className="compare-header-cell">Generierter Text</div>
      </div>
      {sections.map((s, i) => {
        const inputs = buildInputForSection(s.name, inputData, anzeige);
        return (
          <div key={s.name} className="compare-row">
            <div className="compare-section-name">{s.name}</div>
            <div className="compare-row-cells">
              <div className="compare-cell compare-cell--input">
                {inputs.length === 0 && <p className="compare-empty">Keine direkten Eingaben für diesen Abschnitt</p>}
                {inputs.map((inp, j) => {
                  const key = `${s.name}#${j}`;
                  const isChecked = !!checks[key];
                  const showGroup = inp.group && inp.group !== (inputs[j - 1] && inputs[j - 1].group);
                  const meta = inp.meta && inp.meta.length > 0 && (
                    <div className="jf-aufgabe-meta">
                      {inp.meta.map((tag, k) => (
                        <span key={k} className="jf-aufgabe-meta-item">
                          <span className="jf-aufgabe-meta-label">{tag.label}:</span> {tag.value}
                        </span>
                      ))}
                    </div>
                  );
                  return (
                    <React.Fragment key={j}>
                      {showGroup && <div className="compare-input-group">{inp.group}</div>}
                      {inp.noCheck ? (
                        <div className="compare-input-item compare-input-meta">
                          {inp.label && <span className="compare-input-label">{inp.label}</span>}
                          {inp.value && <div className="compare-input-value">{inp.value}</div>}
                          {meta}
                        </div>
                      ) : (
                        <label className={"compare-input-item compare-input-check" + (isChecked ? " is-checked" : "")}>
                          <input
                            type="checkbox"
                            className="compare-check-box"
                            checked={isChecked}
                            onChange={() => toggleCheck(key)}
                          />
                          <div className="compare-input-body">
                            {inp.label && <span className="compare-input-label">{inp.label}</span>}
                            {inp.value && <div className="compare-input-value">{inp.value}</div>}
                            {meta}
                          </div>
                        </label>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
              <div className="compare-cell compare-cell--output compare-cell--editor">
                <SectionBlock
                  section={s}
                  sectionIndex={i}
                  onUpdateSection={onUpdateSection}
                  reviewed={reviewFlags[String(i)]}
                  onToggleReview={onToggleReview}
                  anzeige={anzeige}
                  forceExpanded
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---- UNEQ-Editor: Zielgruppe + Punkt-für-Punkt-Ausformulierung ----
function UneqZgCol({ title, items, onChange }) {
  const list = items || [];
  const update = (i, val) => onChange(list.map((x, idx) => (idx === i ? val : x)));
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const add = () => onChange([...list, ""]);
  return (
    <div className="uneq-zg-col">
      <span className="uneq-zg-col-title">{title}</span>
      <div className="uneq-zg-items">
        {list.map((x, i) => (
          <div key={i} className="uneq-zg-item">
            <input className="uneq-zg-input" value={x} placeholder="…" onChange={(e) => update(i, e.target.value)} />
            <button className="uneq-zg-del" onClick={() => remove(i)} title="Entfernen">&times;</button>
          </div>
        ))}
        <button className="uneq-zg-add" onClick={add}><Plus size={12} /> Punkt</button>
      </div>
    </div>
  );
}

function UneqInputCell({ anzeigeId, sectionName, bullets, onChange }) {
  const [busyIdx, setBusyIdx] = useState(null);

  const update = (idx, patch) => onChange(bullets.map((b, i) => (i === idx ? { ...b, ...patch } : b)));
  const add = () => onChange([...bullets, { raw: "", formuliert: "" }]);
  const remove = (idx) => onChange(bullets.filter((_, i) => i !== idx));

  const formulate = async (idx) => {
    const raw = (bullets[idx]?.raw || "").trim();
    if (!raw) return;
    setBusyIdx(idx);
    try {
      const res = await fetch(`/api/uneq/stellenanzeigen/${anzeigeId}/formulate`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ sectionName, raw }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Fehler");
      update(idx, { formuliert: d.formuliert });
    } catch (err) { alert("Ausformulieren fehlgeschlagen: " + err.message); }
    finally { setBusyIdx(null); }
  };

  return (
    <div className="uneq-bullets">
      {bullets.length === 0 && <p className="compare-empty">Noch keine Stichpunkte – füge welche hinzu und lass sie ausformulieren.</p>}
      {bullets.map((b, idx) => (
        <div key={idx} className={"uneq-bullet" + (b.formuliert ? " is-done" : "")}>
          <textarea
            className="uneq-bullet-input"
            rows={2}
            placeholder="Stichpunkt …"
            value={b.raw}
            onChange={(e) => update(idx, { raw: e.target.value })}
          />
          <div className="uneq-bullet-actions">
            <button className="btn-ai-go" disabled={!b.raw.trim() || busyIdx !== null} onClick={() => formulate(idx)}>
              {busyIdx === idx ? <Loader size={13} className="spin" /> : <Sparkles size={13} />} {b.formuliert ? "Neu" : "Ausformulieren"}
            </button>
            {b.formuliert && <span className="uneq-bullet-done" title="ausformuliert"><Check size={13} /></span>}
            <button className="btn-rule-del" onClick={() => remove(idx)} title="Entfernen"><Trash2 size={13} /></button>
          </div>
        </div>
      ))}
      <button className="uneq-add-bullet" onClick={add}><Plus size={14} /> Stichpunkt</button>
    </div>
  );
}

function UneqBody({ anzeige, sections, onUpdateSection, onReplaceSections, reviewFlags, onToggleReview }) {
  const [inputData, setInputData] = useState(anzeige.input_data || { uneq: true, bullets: {}, zielgruppe: null });
  const [zgLoading, setZgLoading] = useState(false);
  const [bauLoading, setBauLoading] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const saveTimer = useRef(null);
  const inputRef = useRef(inputData);
  const dirtyRef = useRef(false);
  useEffect(() => { inputRef.current = inputData; }, [inputData]);

  // Feste Bausteine automatisch einsetzen, wenn sie in dieser Anzeige noch fehlen
  // (z.B. bei Anzeigen, die vor Einführung der festen Bausteine angelegt wurden).
  const ensuredRef = useRef(false);
  useEffect(() => {
    if (ensuredRef.current) return;
    if (sections.some((s) => s.fixed)) return;
    ensuredRef.current = true;
    fetch(`/api/uneq/stellenanzeigen/${anzeige.id}/bausteine`, { method: "POST", credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && Array.isArray(d.sections)) onReplaceSections(d.sections); })
      .catch(() => {});
  }, []); // nur beim Öffnen prüfen

  // Ausstehende Speicherung beim Verlassen/Wechsel sofort abschicken (kein Datenverlust).
  useEffect(() => () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (dirtyRef.current) {
      fetch(`/api/uneq/stellenanzeigen/${anzeige.id}/input`, {
        method: "PATCH", headers: { "Content-Type": "application/json" }, credentials: "include",
        keepalive: true, body: JSON.stringify({ input_data: inputRef.current }),
      }).catch(() => {});
      dirtyRef.current = false;
    }
  }, [anzeige.id]);

  const refreshBausteine = async () => {
    setBauLoading(true);
    try {
      const res = await fetch(`/api/uneq/stellenanzeigen/${anzeige.id}/bausteine`, { method: "POST", credentials: "include" });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Fehler");
      onReplaceSections(d.sections);
    } catch (err) { alert("Bausteine einsetzen fehlgeschlagen: " + err.message); }
    finally { setBauLoading(false); }
  };

  const persistInput = useCallback((next) => {
    dirtyRef.current = true;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      fetch(`/api/uneq/stellenanzeigen/${anzeige.id}/input`, {
        method: "PATCH", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ input_data: next }),
      })
        .then((r) => {
          // fetch scheitert nur bei Netzwerkfehlern: ein HTTP 500 oder ein 401
          // nach abgelaufener Session landet ebenfalls hier. Nur bei r.ok gilt
          // die Aenderung als gespeichert - sonst bleibt dirtyRef gesetzt,
          // damit sie beim Verlassen der Seite erneut abgeschickt wird.
          if (!r.ok) throw new Error("HTTP " + r.status);
          dirtyRef.current = false;
          setSaveFailed(false);
        })
        .catch(() => setSaveFailed(true));
    }, 800);
  }, [anzeige.id]);

  const analyzeZielgruppe = async () => {
    setZgLoading(true);
    try {
      const res = await fetch(`/api/uneq/stellenanzeigen/${anzeige.id}/zielgruppe`, { method: "POST", credentials: "include" });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Fehler");
      setInputData((prev) => ({ ...prev, zielgruppe: d.zielgruppe })); // serverseitig bereits gespeichert
    } catch (err) { alert("Zielgruppen-Analyse fehlgeschlagen: " + err.message); }
    finally { setZgLoading(false); }
  };

  const handleBulletsChange = (sectionIndex, name, list) => {
    setInputData((prev) => {
      const next = { ...prev, bullets: { ...(prev.bullets || {}), [name]: list } };
      persistInput(next);
      return next;
    });
    // Rechte Seite aus den ausformulierten Stichpunkten neu aufbauen.
    const section = sections[sectionIndex];
    const items = list.filter((b) => b.formuliert && b.formuliert.trim()).map((b) => b.formuliert);
    const updated = { ...section, items };
    updated.content = [updated.headline, updated.einleitung, updated.body, ...items].filter(Boolean).join("\n\n");
    onUpdateSection(sectionIndex, updated);
  };

  const zg = inputData.zielgruppe || { pains: [], wuensche: [], wichtig: [] };
  const hasZg = !!inputData.zielgruppe;
  const isEmptyZg = !((zg.pains || []).length || (zg.wuensche || []).length || (zg.wichtig || []).length);

  const updateZg = (field, list) => {
    setInputData((prev) => {
      const base = prev.zielgruppe || { pains: [], wuensche: [], wichtig: [] };
      const next = { ...prev, zielgruppe: { ...base, [field]: list } };
      persistInput(next);
      return next;
    });
  };

  return (
    <div className="uneq-editor">
      {saveFailed && (
        <div className="uneq-save-error" role="alert">
          Deine letzten Änderungen konnten nicht gespeichert werden. Lass die Seite offen und prüfe
          deine Verbindung – wir versuchen es beim nächsten Tippen erneut. Melde dich neu an, falls
          das Problem bleibt.
        </div>
      )}
      <div className="uneq-zg card">
        <div className="uneq-zg-head">
          <div className="uneq-zg-title"><Target size={15} /> Zielgruppen-Einordnung</div>
          <button className="btn-secondary-sm" disabled={zgLoading} onClick={analyzeZielgruppe}>
            {zgLoading ? <><Loader size={13} className="spin" /> Analysiere …</> : <><Sparkles size={13} /> {hasZg ? "Neu analysieren" : "Zielgruppe analysieren"}</>}
          </button>
        </div>
        {isEmptyZg && (
          <p className="uneq-zg-empty">Lass die KI aus Titel + Farbtyp erste Pains, Wünsche und Werte des idealen Bewerbers ableiten – oder trage sie selbst ein. Du kannst alle Punkte jederzeit bearbeiten.</p>
        )}
        <div className="uneq-zg-cols">
          <UneqZgCol title="Pains" items={zg.pains} onChange={(l) => updateZg("pains", l)} />
          <UneqZgCol title="Wünsche" items={zg.wuensche} onChange={(l) => updateZg("wuensche", l)} />
          <UneqZgCol title="Wichtig" items={zg.wichtig} onChange={(l) => updateZg("wichtig", l)} />
        </div>
      </div>

      <div className="uneq-bausteine-bar">
        <span className="uneq-bausteine-note">Feste UNEQ-Bausteine (grau) werden zentral unter „UNEQ-Werte &amp; Bausteine“ gepflegt.</span>
        <button className="btn-secondary-sm" disabled={bauLoading} onClick={refreshBausteine}>
          {bauLoading ? <><Loader size={13} className="spin" /> Aktualisiere …</> : <><RotateCcw size={13} /> Bausteine aktualisieren</>}
        </button>
      </div>

      <div className="compare-table">
        <div className="compare-header">
          <div className="compare-header-cell">Stichpunkte</div>
          <div className="compare-header-cell">Ausformuliert (UNEQ)</div>
        </div>
        {sections.map((s, i) => s.fixed ? (
          <div key={s.name} className="compare-row compare-row--fixed">
            <div className="compare-section-name">{s.name}</div>
            <div className="compare-row-cells">
              <div className="compare-cell compare-cell--input">
                <div className="uneq-fixed-note">Fester Baustein – zentral gepflegt.</div>
              </div>
              <div className="compare-cell compare-cell--output">
                <div className="uneq-fixed-content">{renderFormattedText(s.content)}</div>
              </div>
            </div>
          </div>
        ) : (
          <div key={s.name} className="compare-row">
            <div className="compare-section-name">{s.name}</div>
            <div className="compare-row-cells">
              <div className="compare-cell compare-cell--input">
                <UneqInputCell
                  anzeigeId={anzeige.id}
                  sectionName={s.name}
                  bullets={(inputData.bullets && inputData.bullets[s.name]) || []}
                  onChange={(list) => handleBulletsChange(i, s.name, list)}
                />
              </div>
              <div className="compare-cell compare-cell--output compare-cell--editor">
                <SectionBlock
                  section={s}
                  sectionIndex={i}
                  onUpdateSection={onUpdateSection}
                  reviewed={reviewFlags[String(i)]}
                  onToggleReview={onToggleReview}
                  anzeige={anzeige}
                  forceExpanded
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StellenDetail({ anzeige, onBack }) {
  const [sections, setSections] = useState(anzeige.sections);
  const [reviewFlags, setReviewFlags] = useState(anzeige.review_flags || {});
  const [reviewing, setReviewing] = useState(false);
  const [reviewInstruction, setReviewInstruction] = useState("");
  const [showReviewInput, setShowReviewInput] = useState(false);
  const [status, setStatus] = useState(anzeige.status || "entwurf");
  const initialMountRef = useRef(true);

  // Undo/Redo-History für Section-Bearbeitungen (Buttons + Strg/Cmd+Z / +Shift+Z).
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const sectionsRef = useRef(sections);
  const historyRef = useRef([]); // Undo-Stack (frühere Stände)
  const futureRef = useRef([]);  // Redo-Stack (rückgängig gemachte Stände)
  useEffect(() => { sectionsRef.current = sections; }, [sections]);

  const commitSections = useCallback((next) => {
    const resolved = typeof next === "function" ? next(sectionsRef.current) : next;
    historyRef.current = [...historyRef.current, sectionsRef.current].slice(-50);
    futureRef.current = []; // neue Änderung verwirft Redo
    sectionsRef.current = resolved; // synchron, falls mehrere Commits pro Tick
    setCanUndo(true);
    setCanRedo(false);
    setSections(resolved);
  }, []);

  const undo = useCallback(() => {
    if (!historyRef.current.length) return;
    const prev = historyRef.current[historyRef.current.length - 1];
    historyRef.current = historyRef.current.slice(0, -1);
    futureRef.current = [...futureRef.current, sectionsRef.current].slice(-50);
    sectionsRef.current = prev;
    setCanUndo(historyRef.current.length > 0);
    setCanRedo(true);
    setSections(prev);
  }, []);

  const redo = useCallback(() => {
    if (!futureRef.current.length) return;
    const nextState = futureRef.current[futureRef.current.length - 1];
    futureRef.current = futureRef.current.slice(0, -1);
    historyRef.current = [...historyRef.current, sectionsRef.current].slice(-50);
    sectionsRef.current = nextState;
    setCanUndo(true);
    setCanRedo(futureRef.current.length > 0);
    setSections(nextState);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const tag = (e.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target.isContentEditable) return; // natives Undo im Eingabefeld
      const k = e.key.toLowerCase();
      if (k === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
      else if ((k === "z" && e.shiftKey) || k === "y") { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo]);

  const updateStatus = useCallback(async (newStatus) => {
    const prev = status;
    setStatus(newStatus);
    try {
      const res = await fetch(`/api/stellenanzeige/${anzeige.id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error();
    } catch {
      setStatus(prev);
      alert("Status konnte nicht gespeichert werden");
    }
  }, [anzeige.id, status]);

  const handleUpdateSection = (idx, result) => {
    commitSections((prev) => prev.map((s, i) => (
      i === idx ? (typeof result === "string" ? { ...s, content: result } : result) : s
    )));
  };

  const handleToggleReview = (idx) => {
    const newFlags = { ...reviewFlags, [String(idx)]: !reviewFlags[String(idx)] };
    setReviewFlags(newFlags);
    // Auto-save the flag
    fetch(`/api/stellenanzeige/${anzeige.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ review_flags: newFlags }),
    }).catch(() => {});
  };

  // Debounced Auto-Save für sections + reviewFlags (still im Hintergrund)
  useEffect(() => {
    if (initialMountRef.current) { initialMountRef.current = false; return; }
    const handle = setTimeout(() => {
      fetch(`/api/stellenanzeige/${anzeige.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ sections, review_flags: reviewFlags }),
      }).catch(() => {});
    }, 1500);
    return () => clearTimeout(handle);
  }, [sections, reviewFlags, anzeige.id]);

  const handleReview = async () => {
    setReviewing(true);
    setShowReviewInput(false);
    try {
      // First save current state
      await fetch(`/api/stellenanzeige/${anzeige.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sections, review_flags: reviewFlags }),
      });
      // Run full agent review
      const res = await fetch(`/api/stellenanzeige/${anzeige.id}/review`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ instruction: reviewInstruction || undefined }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      if (!data.unchanged && data.sections) {
        setSections(data.sections);
        // Reset review flags since text changed
        const newFlags = {};
        data.sections.forEach((_, idx) => { newFlags[String(idx)] = false; });
        setReviewFlags(newFlags);
      }
      setReviewInstruction("");
    } catch (err) {
      alert("Fehler bei der KI-Überarbeitung: " + err.message);
    } finally {
      setReviewing(false);
    }
  };


  const [shareVersions, setShareVersions] = useState([]); // alle Links inkl. revoked
  const [shareLoading, setShareLoading] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);

  const loadShareVersions = useCallback(() => {
    fetch(`/api/stellenanzeige/${anzeige.id}/shares`, { credentials: "include" })
      .then((r) => r.json())
      .then((rows) => { if (Array.isArray(rows)) setShareVersions(rows); })
      .catch(() => {});
  }, [anzeige.id]);

  useEffect(() => { loadShareVersions(); }, [loadShareVersions]);

  const activeVersions = shareVersions.filter((v) => !v.revoked_at);
  const hasActiveLink = activeVersions.length > 0;

  const createShareLink = useCallback(async () => {
    setShareLoading(true);
    try {
      // Vor dem Snapshot speichern, damit der aktuelle Stand drin ist
      await fetch(`/api/stellenanzeige/${anzeige.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ sections, review_flags: reviewFlags }),
      });
      const res = await fetch(`/api/stellenanzeige/${anzeige.id}/share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || "Fehler");
      loadShareVersions();
      setShareModalOpen(true);
    } catch (err) {
      alert("Share-Link konnte nicht erstellt werden: " + err.message);
    } finally {
      setShareLoading(false);
    }
  }, [anzeige.id, sections, reviewFlags, loadShareVersions]);

  const revokeShareVersion = useCallback(async (token) => {
    if (!window.confirm("Diese Version widerrufen? Der Partner kann den Link dann nicht mehr öffnen.")) return;
    await fetch(`/api/stellenanzeige/${anzeige.id}/share/${token}`, {
      method: "DELETE",
      credentials: "include",
    });
    loadShareVersions();
  }, [anzeige.id, loadShareVersions]);

  const buildShareUrl = (token) => `${window.location.origin}/s/${token}`;

  return (
    <>
      <button className="btn-back" onClick={onBack}>Zurück</button>
      <div className="sections-toolbar">
        <div>
          <h3>{anzeige.stellentitel}</h3>
          <span className="detail-meta">{anzeige.unternehmen} · {anzeige.standort || "–"} · {anzeige.farbtyp}</span>
        </div>
        <div className="sa-toolbar-actions">
          {anzeige.jotform_url && (
            <a
              className="sa-toolbar-btn"
              href={anzeige.jotform_url}
              target="_blank"
              rel="noopener noreferrer"
              title="Original-Einreichung in Jotform öffnen"
            >
              <ExternalLink size={14} /> Jotform
            </a>
          )}
          <button
            className="sa-toolbar-btn sa-toolbar-btn--icon"
            onClick={undo}
            disabled={!canUndo}
            title="Rückgängig (Strg/Cmd+Z)"
            aria-label="Rückgängig"
          >
            <Undo2 size={16} />
          </button>
          <button
            className="sa-toolbar-btn sa-toolbar-btn--icon"
            onClick={redo}
            disabled={!canRedo}
            title="Wiederholen (Strg/Cmd+Shift+Z)"
            aria-label="Wiederholen"
          >
            <Redo2 size={16} />
          </button>
          <label className={"sa-toolbar-btn sa-status sa-status--" + status} title="Status der Stellenanzeige">
            <select
              value={status}
              onChange={(e) => updateStatus(e.target.value)}
              className="status-pill-select"
              aria-label="Status"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </label>
          {shareVersions.length > 0 ? (
            <button className="sa-toolbar-btn" onClick={() => setShareModalOpen(true)}>
              <FileUp size={14} /> Share-Links ({shareVersions.length})
            </button>
          ) : (
            <button className="sa-toolbar-btn" onClick={createShareLink} disabled={shareLoading}>
              <FileUp size={14} />
              {shareLoading ? "Erstellt …" : "Share-Link erstellen"}
            </button>
          )}
          <button className="sa-toolbar-btn" onClick={() => setShowReviewInput(!showReviewInput)} disabled={reviewing}>
            <Sparkles size={14} /> KI-Überarbeiten
          </button>
        </div>
      </div>
      {anzeige.agent_feedback?.halluzination?.unresolved && Array.isArray(anzeige.agent_feedback.halluzination.issues) && anzeige.agent_feedback.halluzination.issues.length > 0 && (
        <div className="hall-banner">
          <div className="hall-banner-head">
            <strong>Mögliche Halluzinationen – bitte manuell prüfen</strong>
            <span className="hall-banner-sub">Die KI hat folgende Stellen nicht durch die Eingabedaten belegen können. Sie wurden trotz {anzeige.agent_feedback.halluzination.issues.length > 0 ? "Korrektur-Versuchen" : "Prüfung"} nicht entfernt.</span>
          </div>
          <ul className="hall-banner-list">
            {anzeige.agent_feedback.halluzination.issues.map((h, i) => (
              <li key={i}>
                <span className="hall-section">[{h.section}]</span>{" "}
                <span className="hall-passage">„{h.passage}"</span>
                <div className="hall-evidence">{h.problem}{h.evidence ? ` · Eingabedaten: ${h.evidence}` : ""}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
      {Array.isArray(anzeige.agent_feedback?.screener?.warnings) && anzeige.agent_feedback.screener.warnings.length > 0 && (
        <div className="screener-banner">
          <div className="screener-banner-head">
            <strong>Screener-Hinweise zum Briefing</strong>
            <span className="screener-banner-sub">Vor dem Schreiben wurden im Briefing diese Lücken/Dopplungen gefunden. Die Anzeige wurde trotzdem erstellt – bitte prüfen, ob etwas nachgebessert werden sollte.</span>
          </div>
          <ul className="screener-banner-list">
            {anzeige.agent_feedback.screener.warnings.map((w, i) => (
              <li key={i} className={`screener-sev-${w.severity}`}>
                <span className="screener-field">{w.field}</span>
                <span className="screener-issue">{w.issue}</span>
                {w.suggestion && <span className="screener-suggestion">→ {w.suggestion}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {shareModalOpen && (
        <div className="modal-overlay" onClick={(e) => { if (e.target.classList.contains("modal-overlay")) setShareModalOpen(false); }}>
          <div className="modal share-modal">
            <h3>Share-Versionen</h3>
            <p className="share-modal-desc">
              Jede Version friert den aktuellen Text der Stellenanzeige als Snapshot ein. Kommentare bleiben pro Version erhalten — auch wenn Du den Text später überarbeitest.
            </p>
            <button
              type="button"
              className="btn-share-link share-modal-new-btn"
              onClick={createShareLink}
              disabled={shareLoading}
            >
              <FileUp size={14} /> {shareLoading ? "Erstellt …" : "Neue Version erstellen"}
            </button>
            <div className="share-version-list">
              {shareVersions.length === 0 && (
                <div className="share-version-empty">Noch keine Versionen. Erstelle die erste oben.</div>
              )}
              {shareVersions.map((v, i) => {
                const versionNumber = shareVersions.length - i; // V1 = älteste
                const url = buildShareUrl(v.token);
                const revoked = !!v.revoked_at;
                const open = parseInt(v.open_count, 10) || 0;
                const total = parseInt(v.comment_count, 10) || 0;
                return (
                  <div key={v.token} className={"share-version" + (revoked ? " share-version--revoked" : "")}>
                    <div className="share-version-head">
                      <div className="share-version-meta">
                        <span className="share-version-tag">V{versionNumber}</span>
                        <span className="share-version-date">
                          {new Date(v.created_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </span>
                        {revoked ? (
                          <span className="share-version-status share-version-status--revoked">Widerrufen</span>
                        ) : (
                          <span className="share-version-status share-version-status--active">Aktiv</span>
                        )}
                      </div>
                      <div className="share-version-stats">
                        {total > 0 ? (
                          <span title={`${open} offen / ${total} insgesamt`}>
                            {open}/{total} Kommentare
                          </span>
                        ) : (
                          <span style={{ color: "var(--fg-4)" }}>Keine Kommentare</span>
                        )}
                      </div>
                    </div>
                    <div className="share-version-link-row">
                      <input type="text" readOnly value={url} onFocus={(e) => e.target.select()} />
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        onClick={() => navigator.clipboard.writeText(url)}
                        title="Link kopieren"
                      >Kopieren</button>
                      <a
                        className="btn-primary btn-sm"
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >Öffnen</a>
                      {!revoked && (
                        <button
                          type="button"
                          className="btn-danger-text btn-sm"
                          onClick={() => revokeShareVersion(v.token)}
                        >Widerrufen</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="share-modal-actions">
              <button type="button" className="btn-secondary" onClick={() => setShareModalOpen(false)}>Schließen</button>
            </div>
          </div>
        </div>
      )}
      {showReviewInput && (
        <div className="review-input-bar card">
          <input
            type="text"
            className="review-instruction-input"
            placeholder="Optionale Anweisung für die KI (z.B. 'Kürze die Aufgaben' oder leer lassen für automatische Prüfung)..."
            value={reviewInstruction}
            onChange={(e) => setReviewInstruction(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleReview()}
            autoFocus
          />
          <div className="refine-actions">
            <button className="btn-refine-go" onClick={handleReview} disabled={reviewing}>
              {reviewing ? "Wird überarbeitet..." : "Agenten-Kette starten"}
            </button>
            <button className="btn-refine-cancel" onClick={() => { setShowReviewInput(false); setReviewInstruction(""); }}>
              Abbrechen
            </button>
          </div>
        </div>
      )}
      {reviewing && (
        <div className="review-overlay">
          <div className="spinner" />
          <span>KI-Agenten überarbeiten die Stellenanzeige...</span>
        </div>
      )}
      {anzeige.source === "uneq" ? (
        <UneqBody
          anzeige={anzeige}
          sections={sections}
          onUpdateSection={handleUpdateSection}
          onReplaceSections={setSections}
          reviewFlags={reviewFlags}
          onToggleReview={handleToggleReview}
        />
      ) : (
        <ComparisonTable
          sections={sections}
          inputData={anzeige.input_data}
          anzeige={anzeige}
          onUpdateSection={handleUpdateSection}
          reviewFlags={reviewFlags}
          onToggleReview={handleToggleReview}
        />
      )}
      <div className="backups-link-row">
        <button className="btn-secondary" onClick={() => {
          navigate(`/jobs/backups/${anzeige.id}`);
        }}>
          <HardDrive size={14} />
          Änderungsverlauf anzeigen
        </button>
      </div>
    </>
  );
}

// --- Partner archive view ---

function AnzeigeRow({ anzeige, onClick, onDelete, onRegenerate, isRegenerating }) {
  const handleDelete = (e) => {
    e.stopPropagation();
    if (onDelete) onDelete(anzeige);
  };
  const handleRegenerate = (e) => {
    e.stopPropagation();
    if (onRegenerate) onRegenerate(anzeige);
  };
  return (
    <div className="archive-row card" onClick={onClick}>
      <div className="archive-info">
        <h4>{anzeige.stellentitel}</h4>
        <span className="archive-meta">
          {anzeige.farbtyp} · {anzeige.source === "jotform" ? "Jotform" : "Manuell"} · {formatDate(anzeige.created_at)}
        </span>
      </div>
      <div className="archive-actions">
        {(anzeige.cost_eur != null || anzeige.total_tokens != null) && (
          <span className="archive-cost" data-tooltip="Generierungskosten">
            {anzeige.cost_eur != null ? formatEur(anzeige.cost_eur) : formatCost(anzeige.total_tokens)}
          </span>
        )}
        <span className={"status-pill status-pill--" + (anzeige.status || "entwurf") + " status-pill--readonly"}>
          {statusLabel(anzeige.status || "entwurf")}
        </span>
        <button className="btn-icon btn-edit" onClick={onClick} data-tooltip="Bearbeiten">
          <Pencil size={15} />
        </button>
        {onRegenerate && (
          <button
            className="btn-icon"
            onClick={handleRegenerate}
            disabled={isRegenerating}
            data-tooltip="Neu generieren"
          >
            <RotateCcw size={15} className={isRegenerating ? "spinning" : ""} />
          </button>
        )}
        <button className="btn-icon btn-delete" onClick={handleDelete} data-tooltip="Löschen">
          <Trash2 size={15} />
        </button>
      </div>
    </div>
  );
}

function ProjectGroup({ stellentitel, anzeigen, onSelectAnzeige, onDelete, onRegenerate, regeneratingIds }) {
  const [expanded, setExpanded] = useState(true);
  return (
    <div className="project-group">
      <div className="project-header" onClick={() => setExpanded(!expanded)}>
        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <h3 className="project-title">{stellentitel}</h3>
        <span className="project-count">{anzeigen.length}</span>
      </div>
      {expanded && (
        <div className="project-items">
          {anzeigen.map((a) => (
            <AnzeigeRow
              key={a.id}
              anzeige={a}
              onClick={() => onSelectAnzeige(a)}
              onDelete={onDelete}
              onRegenerate={onRegenerate}
              isRegenerating={regeneratingIds && regeneratingIds.has(a.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PartnerArchive({ partner, onBack, onSelectAnzeige, activeJobs, onStartGenerate, onRegenerate }) {
  const [anzeigen, setAnzeigen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [sortBy, setSortBy] = useState("date-desc");
  const [regeneratingIds, setRegeneratingIds] = useState(new Set());

  const loadAnzeigen = useCallback((isRefresh) => {
    if (!isRefresh) setLoading(true);
    fetch(`/api/partners/${encodeURIComponent(partner.partner_id)}/stellenanzeigen`)
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data)) setAnzeigen(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [partner.partner_id]);

  useEffect(() => { loadAnzeigen(); }, [loadAnzeigen]);

  // Auto-refresh when active jobs complete
  const prevJobCount = React.useRef((activeJobs || []).filter((j) => j.partner_id === partner.partner_id).length);
  useEffect(() => {
    const currentCount = (activeJobs || []).filter((j) => j.partner_id === partner.partner_id).length;
    if (currentCount < prevJobCount.current && prevJobCount.current > 0) {
      loadAnzeigen(true);
    }
    prevJobCount.current = currentCount;
  }, [activeJobs, partner.partner_id, loadAnzeigen]);

  const handleStartGenerate = (sub) => {
    setShowModal(false);
    if (onStartGenerate) onStartGenerate(sub, partner);
  };

  const handleDelete = async (anzeige) => {
    if (!window.confirm(`"${anzeige.stellentitel}" wirklich löschen? Die Anzeige wird für 30 Tage im Papierkorb aufbewahrt.`)) return;
    try {
      await fetch(`/api/stellenanzeige/${anzeige.id}`, { method: "DELETE" });
      loadAnzeigen();
    } catch (err) {
      console.error("Delete error:", err);
    }
  };

  const handleRegenerate = async (anzeige) => {
    if (!window.confirm(`"${anzeige.stellentitel}" mit denselben Eingabedaten neu generieren? Eine neue Stellenanzeige wird erstellt; die bestehende bleibt unverändert.`)) return;
    setRegeneratingIds((prev) => new Set(prev).add(anzeige.id));
    try {
      await onRegenerate(anzeige, partner);
    } catch (err) {
      console.error("Regenerate error:", err);
    } finally {
      setRegeneratingIds((prev) => {
        const next = new Set(prev);
        next.delete(anzeige.id);
        return next;
      });
    }
  };

  // Sort helper
  const sortFn = useCallback((a, b) => {
    switch (sortBy) {
      case "name-asc": return a.stellentitel.localeCompare(b.stellentitel, "de");
      case "name-desc": return b.stellentitel.localeCompare(a.stellentitel, "de");
      case "date-asc": return new Date(a.created_at) - new Date(b.created_at);
      case "date-desc":
      default: return new Date(b.created_at) - new Date(a.created_at);
    }
  }, [sortBy]);

  // Flat sorted list (no grouping) for display
  const sortedAnzeigen = useMemo(() => {
    return [...anzeigen].sort(sortFn);
  }, [anzeigen, sortFn]);

  const partnerJobs = (activeJobs || []).filter((j) => j.partner_id === partner.partner_id);

  return (
    <>
      <button className="btn-back" onClick={onBack}>Zurück zur Übersicht</button>
      <PageHeader
        eyebrow="Partner"
        title={partner.unternehmen}
        subtitle={`Archiv – ${anzeigen.length} Stellenanzeige${anzeigen.length !== 1 ? "n" : ""}`}
        actions={
          <button className="btn-primary" onClick={() => setShowModal(true)}>
            <Plus size={16} />
            Neue Stellenanzeige
          </button>
        }
      />

      {/* Active generation jobs */}
      {partnerJobs.length > 0 && (
        <div className="generating-jobs">
          {partnerJobs.map((job) => (
            <div className="generating-row card" key={job.id}>
              <Loader size={16} className="spinning" />
              <div className="generating-info">
                <span className="generating-title">{job.stellentitel}</span>
                <PipelineProgress stage={job.stage || "agent1"} createdAt={job.created_at} avgSec={job.avg_duration_sec} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Sort toolbar */}
      {!loading && anzeigen.length > 1 && (
        <div className="sort-toolbar">
          <ArrowUpDown size={14} />
          <span className="sort-label">Sortieren:</span>
          <button className={`sort-btn ${sortBy === "date-desc" ? "active" : ""}`} onClick={() => setSortBy("date-desc")}>Neueste zuerst</button>
          <button className={`sort-btn ${sortBy === "date-asc" ? "active" : ""}`} onClick={() => setSortBy("date-asc")}>Älteste zuerst</button>
          <button className={`sort-btn ${sortBy === "name-asc" ? "active" : ""}`} onClick={() => setSortBy("name-asc")}>Name A–Z</button>
          <button className={`sort-btn ${sortBy === "name-desc" ? "active" : ""}`} onClick={() => setSortBy("name-desc")}>Name Z–A</button>
        </div>
      )}

      {loading ? (
        <div className="loading-indicator"><div className="spinner" /><span>Lade Archiv …</span></div>
      ) : anzeigen.length === 0 && partnerJobs.length === 0 ? (
        <div className="empty-card card">
          <FolderOpen size={32} strokeWidth={1.5} />
          <p style={{ marginTop: 12 }}>Noch keine Stellenanzeigen vorhanden. Erstelle die erste über eine Jotform-Einreichung.</p>
        </div>
      ) : (() => {
        // Group by Stellentitel
        const groups = {};
        for (const a of sortedAnzeigen) {
          const key = a.stellentitel || "Ohne Titel";
          if (!groups[key]) groups[key] = [];
          groups[key].push(a);
        }
        const groupKeys = Object.keys(groups);
        // If all have unique titles, show flat list
        const hasGroups = groupKeys.some(k => groups[k].length > 1);
        if (!hasGroups) {
          return (
            <div className="archive-list">
              {sortedAnzeigen.map((a) => (
                <AnzeigeRow key={a.id} anzeige={a} onClick={() => onSelectAnzeige(a)} onDelete={handleDelete} onRegenerate={handleRegenerate} isRegenerating={regeneratingIds.has(a.id)} />
              ))}
            </div>
          );
        }
        return (
          <div className="archive-list">
            {groupKeys.map((titel) => (
              groups[titel].length === 1 ? (
                <AnzeigeRow key={groups[titel][0].id} anzeige={groups[titel][0]} onClick={() => onSelectAnzeige(groups[titel][0])} onDelete={handleDelete} onRegenerate={handleRegenerate} isRegenerating={regeneratingIds.has(groups[titel][0].id)} />
              ) : (
                <ProjectGroup key={titel} stellentitel={titel} anzeigen={groups[titel]} onSelectAnzeige={onSelectAnzeige} onDelete={handleDelete} onRegenerate={handleRegenerate} regeneratingIds={regeneratingIds} />
              )
            ))}
          </div>
        );
      })()}

      {showModal && (
        <JotformModal
          onClose={() => setShowModal(false)}
          onStartGenerate={handleStartGenerate}
          filterUnternehmen={partner.unternehmen}
        />
      )}
    </>
  );
}

// --- Pipeline progress labels ---

const STAGE_LABELS = {
  screener: { label: "Briefing-Check...", pct: 4 },
  reference: { label: "Stil-Referenz...", pct: 7 },
  agent1: { label: "Schreiben...", pct: 10 },
  "agents2-8": { label: "Prüfung (Durchlauf 1)...", pct: 20 },
  agent7: { label: "Überarbeitung (Durchlauf 1)...", pct: 30 },
  "hallucination-check": { label: "Faktentreue-Gate...", pct: 90 },
  "content-check": { label: "Inhalt & Ausdruck prüfen...", pct: 93 },
  "content-rewrite": { label: "Ausdruck-Feinschliff...", pct: 94 },
  formatting: { label: "Formatierung...", pct: 95 },
  done: { label: "Fertig!", pct: 100 },
};

function PipelineProgress({ stage, createdAt, avgSec }) {
  // Handle dynamic review/rewrite stages for unlimited iterations
  let info = STAGE_LABELS[stage];
  if (!info) {
    const reviewMatch = stage && stage.match(/^review-(\d+)$/);
    const rewriteMatch = stage && stage.match(/^rewrite-(\d+)$/);
    const hallCheckMatch = stage && stage.match(/^hallucination-check-(\d+)$/);
    const hallRewriteMatch = stage && stage.match(/^hall-rewrite-(\d+)$/);
    if (reviewMatch) {
      const n = parseInt(reviewMatch[1]);
      info = { label: `Prüfung (Durchlauf ${n})...`, pct: Math.min(90, 20 + n * 10) };
    } else if (rewriteMatch) {
      const n = parseInt(rewriteMatch[1]);
      info = { label: `Überarbeitung (Durchlauf ${n})...`, pct: Math.min(95, 25 + n * 10) };
    } else if (hallCheckMatch) {
      const n = parseInt(hallCheckMatch[1]);
      info = { label: `Faktentreue-Gate (Durchlauf ${n})...`, pct: 90 };
    } else if (hallRewriteMatch) {
      const n = parseInt(hallRewriteMatch[1]);
      info = { label: `Faktentreue-Korrektur (Durchlauf ${n})...`, pct: 92 };
    } else {
      info = STAGE_LABELS.agent1;
    }
  }
  return (
    <div className="pipeline-progress">
      <div className="pipeline-bar-track">
        <div className="pipeline-bar-fill" style={{ width: `${info.pct}%` }} />
      </div>
      <div className="pipeline-label">
        <span className="pipeline-step">{info.label}</span>
        {stage !== "done" && generationEta(createdAt, avgSec).text && (
          <span className="pipeline-eta">{generationEta(createdAt, avgSec).text}</span>
        )}
      </div>
    </div>
  );
}

// --- Jotform modal (starts generation in background) ---

function JotformModal({ onClose, onStartGenerate, filterUnternehmen }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [started, setStarted] = useState(new Set());
  const [checking, setChecking] = useState(null);
  const [checkResult, setCheckResult] = useState(null);
  const [expandedSub, setExpandedSub] = useState(null);
  const [farbtypConfirmed, setFarbtypConfirmed] = useState({});
  const [farbtypOverride, setFarbtypOverride] = useState({});
  // Persönlichkeits-Empfehlung des Agenten für die gerade aufgeklappte Einreichung.
  const [pers, setPers] = useState(null); // { submissionId, state: "loading"|"error"|"ok", data }
  const [persWhyOpen, setPersWhyOpen] = useState(false); // Begründung ein-/ausklappen

  useEffect(() => {
    const url = filterUnternehmen
      ? `/api/jotform/submissions?unternehmen=${encodeURIComponent(filterUnternehmen)}`
      : "/api/jotform/submissions";
    fetch(url)
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data)) setSubmissions(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [filterUnternehmen]);

  const runPersonality = async (sub) => {
    setPers({ submissionId: sub.submissionId, state: "loading" });
    try {
      const r = await fetch(`/api/jotform/submissions/${sub.submissionId}/personality`, { method: "POST", credentials: "include" });
      const j = await r.json().catch(() => ({}));
      setPers({ submissionId: sub.submissionId, state: r.ok ? "ok" : "error", data: j });
    } catch {
      setPers({ submissionId: sub.submissionId, state: "error" });
    }
  };

  const handleExpand = (sub) => {
    if (started.has(sub.submissionId)) return;
    const opening = expandedSub !== sub.submissionId;
    setExpandedSub(opening ? sub.submissionId : null);
    setCheckResult(null);
    setPersWhyOpen(false);
    // Persönlichkeitstyp beim Aufklappen einmalig kritisch prüfen.
    if (opening) runPersonality(sub);
  };

  const handleCheck = async (sub) => {
    setChecking(sub.submissionId);
    setCheckResult(null);
    try {
      const res = await fetch(`/api/jotform/submissions/${sub.submissionId}/check`, { method: "POST" });
      const data = await res.json();
      if (data.ok && (!data.warnings || data.warnings.length === 0)) {
        handleGenerate(sub);
      } else {
        setCheckResult({ submissionId: sub.submissionId, sub, ...data });
      }
    } catch (err) {
      handleGenerate(sub);
    } finally {
      setChecking(null);
    }
  };

  const handleGenerate = (sub) => {
    const override = farbtypOverride[sub.submissionId];
    const finalSub = override && override !== normalizeFarbtypFE(sub.farbtyp)
      ? { ...sub, farbtypOverride: override }
      : { ...sub };
    // Screener-Funde dieser Einreichung an den Generierungs-Start weiterreichen.
    if (checkResult && checkResult.submissionId === sub.submissionId && Array.isArray(checkResult.warnings) && checkResult.warnings.length > 0) {
      finalSub.screener = { ok: checkResult.ok !== false, warnings: checkResult.warnings };
    }
    setStarted((prev) => new Set(prev).add(sub.submissionId));
    setCheckResult(null);
    setExpandedSub(null);
    onStartGenerate(finalSub);
  };

  const normalizeFarbtypFE = (raw) => {
    if (!raw) return "rot-gelb";
    const key = raw.toLowerCase().replace(/ü/g, "ue").replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ß/g, "ss").replace(/\s+/g, "-").replace(/[^a-z-]/g, "");
    return FARBTYP_OPTIONS.find((o) => o.value === key) ? key : "rot-gelb";
  };
  const getEffectiveFarbtyp = (sub) => farbtypOverride[sub.submissionId] || normalizeFarbtypFE(sub.farbtyp);

  const isFarbtypOk = (subId) => farbtypConfirmed[subId] === true;

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3>{filterUnternehmen ? `Einreichung für ${filterUnternehmen}` : "Jotform-Einreichung auswählen"}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {loading ? (
            <div className="loading-indicator"><div className="spinner" /><span>Lade Einreichungen …</span></div>
          ) : submissions.length === 0 ? (
            <p className="empty-state">Keine Einreichungen gefunden.</p>
          ) : (
            <div className="submission-list">
              {submissions.map((sub) => (
                <div key={sub.submissionId}>
                  <div
                    className={`submission-row ${started.has(sub.submissionId) ? "generating" : ""} ${checking === sub.submissionId ? "checking" : ""} ${expandedSub === sub.submissionId ? "submission-expanded" : ""}`}
                    onClick={() => handleExpand(sub)}
                  >
                    <div className="submission-info">
                      <h4>{sub.stellentitel || "Ohne Titel"}</h4>
                      <span className="submission-meta">
                        {sub.unternehmen || "–"} · {sub.standort || "–"} · {formatDate(sub.createdAt)}
                      </span>
                    </div>
                    {started.has(sub.submissionId) ? (
                      <span className="submission-action" style={{ color: "#9E9BA8" }}>Gestartet</span>
                    ) : (
                      <span className="submission-action">{expandedSub === sub.submissionId ? "▾" : "▸"}</span>
                    )}
                  </div>

                  {expandedSub === sub.submissionId && !started.has(sub.submissionId) && (
                    <div className="submission-detail-panel">
                      {/* Full form data display */}
                      {sub.inputData && (() => {
                        const d = sub.inputData;
                        const aufgabenBlocks = parseAufgabenBlocks(d.aufgaben);
                        // Parse Stärken/HardSkills/Schwächen from anforderungen for older data
                        let staerken = d.staerken || "";
                        let hardSkills = d.hardSkills || "";
                        let schwaechen = d.schwaechen || "";
                        if (!staerken && !hardSkills && !schwaechen && d.anforderungen) {
                          const anf = d.anforderungen;
                          const stMatch = anf.match(/Stärken:\s*([\s\S]*?)(?=\n\nHard Skills:|$)/);
                          const hsMatch = anf.match(/Hard Skills:\s*([\s\S]*?)(?=\n\nAkzeptable Schwächen:|$)/);
                          const swMatch = anf.match(/Akzeptable Schwächen:\s*([\s\S]*?)$/);
                          if (stMatch) staerken = stMatch[1].trim();
                          if (hsMatch) hardSkills = hsMatch[1].trim();
                          if (swMatch) schwaechen = swMatch[1].trim();
                        }
                        const formSections = [
                          { key: "aufgaben", label: "Aufgaben", data: aufgabenBlocks, type: "aufgaben" },
                          { key: "staerken", label: "Stärken", data: staerken },
                          { key: "hardskills", label: "Hard Skills", data: hardSkills },
                          { key: "schwaechen", label: "Schwächen", data: schwaechen },
                          { key: "disqualifikation", label: "Disqualifikation", data: d.disqualifikation },
                        ].filter((s) => s.type === "aufgaben" ? s.data.length > 0 : Boolean(s.data));
                        // Benefits grouped by personality type
                        const bbt = d.benefitsByType || {};
                        const benefitGroups = Object.entries(BENEFIT_TYPE_LABELS)
                          .filter(([key]) => bbt[key])
                          .map(([key, label]) => ({ key, label, data: bbt[key] }));
                        return (
                          <div className="submission-form-data">
                            <h4 className="submission-form-title">Formulardaten</h4>
                            {d.zusatzinfos && (
                              <div className="submission-form-meta">
                                {d.zusatzinfos.replace(/^Arbeitszeit:\s*/i, "").trim() && (
                                  <span>Arbeitszeit: {d.zusatzinfos.replace(/^Arbeitszeit:\s*/i, "").trim()}</span>
                                )}
                              </div>
                            )}
                            {formSections.map((s) => (
                              <div key={s.key} className="submission-form-section">
                                <div className="submission-form-label">{s.label}</div>
                                {s.type === "aufgaben" ? (
                                  <div className="submission-form-aufgaben">
                                    {s.data.map((block, i) => (
                                      <div key={i} className="submission-form-aufgabe">
                                        <strong>Aufgabe {block.num}{block.pct ? ` · ${block.pct}%` : ""}</strong>
                                        <div className="submission-form-detail">{block.description}</div>
                                        {block.tags.length > 0 && (
                                          <div className="jf-aufgabe-tags" style={{ marginTop: "4px" }}>
                                            {block.tags.map((tag, j) => (
                                              <span key={j} className="jf-aufgabe-tag">{tag.value}</span>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="submission-form-text">{s.data}</div>
                                )}
                              </div>
                            ))}
                            {/* Benefits grouped by personality type */}
                            {benefitGroups.length > 0 ? (
                              <div className="submission-form-section">
                                <div className="submission-form-label">Benefits</div>
                                <div className="benefits-by-type">
                                  {benefitGroups.map((g) => (
                                    <div key={g.key} className={`benefit-type-group benefit-type-${g.key}`}>
                                      <div className="benefit-type-label">{g.label}</div>
                                      <div className="benefit-type-text">{g.data}</div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : d.benefits ? (
                              <div className="submission-form-section">
                                <div className="submission-form-label">Benefits</div>
                                <div className="submission-form-text">{d.benefits}</div>
                              </div>
                            ) : null}
                          </div>
                        );
                      })()}

                      {/* Farbtyp confirmation with change option */}
                      <div className="farbtyp-confirm">
                        <div className="farbtyp-confirm-header">
                          <span className="farbtyp-confirm-label">Farbtyp prüfen</span>
                        </div>
                        {/* Persönlichkeitstyp-Agent: Empfehlung + Übernehmen (überschreibt erst auf Klick) */}
                        {pers && pers.submissionId === sub.submissionId && (
                          <div className="farbtyp-pers">
                            {pers.state === "loading" && (
                              <div className="farbtyp-pers-loading">Persönlichkeitstyp wird geprüft …</div>
                            )}
                            {pers.state === "error" && (
                              <div className="farbtyp-pers-err">
                                Prüfung fehlgeschlagen. <button type="button" className="inbox-link" onClick={(e) => { e.stopPropagation(); runPersonality(sub); }}>Erneut prüfen</button>
                              </div>
                            )}
                            {pers.state === "ok" && pers.data && pers.data.recommended && (() => {
                              const submittedKey = pers.data.submitted?.key || normalizeFarbtypFE(sub.farbtyp);
                              const recKey = pers.data.recommended.key;
                              const why = pers.data.reasoning || pers.data.assessment;
                              return (
                              <div className={"farbtyp-pers-result" + (pers.data.agreement ? " is-match" : " is-diff")}>
                                <div className="farbtyp-pers-row">
                                  <span className="farbtyp-pers-tag">Eingereicht</span>
                                  <FarbtypDots value={submittedKey} />
                                  <strong className="farbtyp-pers-name">{pers.data.submitted?.label || sub.farbtyp || "–"}</strong>
                                  {pers.data.agreement && <span className="farbtyp-pers-verdict ok">✓ passt</span>}
                                </div>
                                {!pers.data.agreement && (
                                  <div className="farbtyp-pers-row farbtyp-pers-row--rec">
                                    <span className="farbtyp-pers-tag">Empfohlen</span>
                                    <FarbtypDots value={recKey} />
                                    <strong className="farbtyp-pers-name">{pers.data.recommended.label}</strong>
                                    <button
                                      type="button"
                                      className="btn-secondary-sm farbtyp-pers-apply"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setFarbtypOverride((prev) => ({ ...prev, [sub.submissionId]: recKey }));
                                        setFarbtypConfirmed((prev) => ({ ...prev, [sub.submissionId]: true }));
                                      }}
                                    >
                                      Übernehmen
                                    </button>
                                  </div>
                                )}
                                {why && (
                                  <div className="farbtyp-pers-why">
                                    <button type="button" className="farbtyp-pers-why-toggle" onClick={(e) => { e.stopPropagation(); setPersWhyOpen((o) => !o); }}>
                                      <span className="farbtyp-why-chevron">{persWhyOpen ? "▾" : "▸"}</span> Warum diese Empfehlung?
                                    </button>
                                    {persWhyOpen && <p className="farbtyp-pers-text">{why}</p>}
                                  </div>
                                )}
                              </div>
                              );
                            })()}
                          </div>
                        )}
                        <div className="farbtyp-select-row">
                          <select
                            className="farbtyp-select"
                            value={getEffectiveFarbtyp(sub)}
                            onChange={(e) => {
                              setFarbtypOverride((prev) => ({ ...prev, [sub.submissionId]: e.target.value }));
                              setFarbtypConfirmed((prev) => ({ ...prev, [sub.submissionId]: false }));
                            }}
                          >
                            {FARBTYP_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                          {farbtypOverride[sub.submissionId] && farbtypOverride[sub.submissionId] !== sub.farbtyp && (
                            <span className="farbtyp-changed-hint">Geändert (Original: {sub.farbtyp || "–"})</span>
                          )}
                        </div>
                        <label className="farbtyp-checkbox">
                          <input
                            type="checkbox"
                            checked={isFarbtypOk(sub.submissionId)}
                            onChange={(e) => setFarbtypConfirmed((prev) => ({ ...prev, [sub.submissionId]: e.target.checked }))}
                          />
                          <span>Farbtyp geprüft und korrekt</span>
                        </label>
                      </div>

                      {/* Schritt 1: Prüf-Ergebnis */}
                      {checkResult && checkResult.submissionId === sub.submissionId && checkResult.warnings && checkResult.warnings.length > 0 && (
                        <div className="check-warnings">
                          <h4 className="check-title"><AlertTriangle size={14} /> Briefing-Prüfung: {checkResult.ok ? "Hinweise gefunden" : "Probleme gefunden"}</h4>
                          <p className="check-subtitle">Du kannst die Hinweise einarbeiten (Formular) oder trotzdem generieren.</p>
                          {checkResult.warnings.map((w, i) => (
                            <div key={i} className={`check-warning severity-${w.severity}`}>
                              <span className="check-field">{w.field}</span>
                              <span className="check-issue">{w.issue}</span>
                              {w.suggestion && <span className="check-suggestion">→ {w.suggestion}</span>}
                            </div>
                          ))}
                          <div className="check-actions" style={{ marginTop: 12 }}>
                            <button className="btn-primary" onClick={(e) => { e.stopPropagation(); handleGenerate(sub); }}>
                              <Sparkles size={15} /> Trotzdem generieren
                            </button>
                            <button className="btn-secondary" onClick={(e) => { e.stopPropagation(); setCheckResult(null); }}>
                              Abbrechen
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Prüfen & generieren */}
                      {!(checkResult && checkResult.submissionId === sub.submissionId && checkResult.warnings && checkResult.warnings.length > 0) && (
                        <div className="submission-footer">
                          <button
                            className="btn-primary submission-generate-btn"
                            disabled={!isFarbtypOk(sub.submissionId) || checking === sub.submissionId}
                            onClick={(e) => { e.stopPropagation(); handleCheck(sub); }}
                          >
                            {checking === sub.submissionId
                              ? <><Loader size={15} className="spin" /> Prüfe Briefing …</>
                              : <><Sparkles size={15} /> Briefing prüfen &amp; generieren</>}
                          </button>
                          <span className="submission-footer-hint">
                            {!isFarbtypOk(sub.submissionId)
                              ? "Bitte zuerst den Farbtyp bestätigen."
                              : "1. Das Briefing wird auf Lücken geprüft  →  2. Ist alles sauber, startet die Generierung automatisch; sonst zeigen wir dir die Hinweise."}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Legacy check result panel for backwards compat - now inline above */}
                  {checkResult && checkResult.submissionId === sub.submissionId && expandedSub !== sub.submissionId && (
                    <div className="check-result-panel">
                      {checkResult.warnings && checkResult.warnings.length > 0 && (
                        <div className="check-warnings">
                          <h4 className="check-title">{checkResult.ok ? "Hinweise zur Einreichung" : "Probleme gefunden"}</h4>
                          {checkResult.warnings.map((w, i) => (
                            <div key={i} className={`check-warning severity-${w.severity}`}>
                              <span className="check-field">{w.field}</span>
                              <span className="check-issue">{w.issue}</span>
                              {w.suggestion && <span className="check-suggestion">→ {w.suggestion}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="check-actions">
                        <button className="btn-primary" onClick={() => handleGenerate(checkResult.sub)}>
                          Trotzdem generieren
                        </button>
                        <button className="btn-secondary" onClick={() => setCheckResult(null)}>
                          Abbrechen
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// --- New Project Modal ---

function NewProjectModal({ onClose, onCreate }) {
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetch("/api/partners/available")
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data)) setAvailable(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setCreating(true);
    setError("");
    try {
      const res = await fetch("/api/partners", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ unternehmen: selected }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      onCreate(data);
    } catch (err) {
      setError(err.message);
      setCreating(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <h3>Neues Projekt anlegen</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {loading ? (
            <div className="loading-indicator"><div className="spinner" /><span>Lade Unternehmen …</span></div>
          ) : available.length === 0 ? (
            <p className="empty-state">Keine neuen Unternehmen gefunden. Alle Jotform-Einreichungen haben bereits ein Projekt.</p>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="project-select">Unternehmen aus Jotform</label>
                <select
                  id="project-select"
                  value={selected}
                  onChange={(e) => setSelected(e.target.value)}
                  autoFocus
                  required
                >
                  <option value="">– Unternehmen auswählen –</option>
                  {available.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
              {error && <p style={{ color: "#e74c3c", fontSize: 13, margin: "8px 0" }}>{error}</p>}
              <button type="submit" className="btn-primary" disabled={creating || !selected} style={{ width: "100%", marginTop: 8 }}>
                {creating ? "Wird erstellt..." : "Projekt erstellen"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Papierkorb ---

function Papierkorb({ onBack }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadItems = useCallback(() => {
    setLoading(true);
    fetch("/api/papierkorb")
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data)) setItems(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { loadItems(); }, [loadItems]);

  const handleRestore = async (item) => {
    try {
      await fetch(`/api/stellenanzeige/${item.id}/restore`, { method: "POST" });
      loadItems();
    } catch (err) {
      console.error("Restore error:", err);
    }
  };

  const daysLeft = (deletedAt) => {
    const diff = 30 - Math.floor((Date.now() - new Date(deletedAt).getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  return (
    <>
      <button className="btn-back" onClick={onBack}>Zurück zur Übersicht</button>
      <PageHeader
        eyebrow="Texte"
        title="Papierkorb"
        subtitle="Gelöschte Stellenanzeigen werden nach 30 Tagen endgültig entfernt."
      />

      {loading ? (
        <div className="loading-indicator"><div className="spinner" /><span>Lade Papierkorb …</span></div>
      ) : items.length === 0 ? (
        <div className="empty-card card">
          <Trash2 size={32} strokeWidth={1.5} />
          <p style={{ marginTop: 12 }}>Der Papierkorb ist leer.</p>
        </div>
      ) : (
        <div className="archive-list">
          {items.map((item) => (
            <div className="archive-row card papierkorb-row" key={item.id}>
              <div className="archive-info">
                <h4>{item.stellentitel}</h4>
                <span className="archive-meta">
                  {item.unternehmen} · {item.farbtyp} · gelöscht am {formatDate(item.deleted_at)} · noch {daysLeft(item.deleted_at)} Tage
                </span>
              </div>
              <button className="btn-icon btn-restore" onClick={() => handleRestore(item)} data-tooltip="Wiederherstellen">
                <RotateCcw size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// --- Backups View ---

const FIELD_LABELS = {
  headline: "Überschrift",
  body: "Text",
  einleitung: "Einleitung",
  content: "Inhalt",
  items: "Liste",
};

function truncate(s, max = 140) {
  const t = String(s || "");
  return t.length > max ? t.slice(0, max).trimEnd() + "…" : t;
}

function diffSections(prev, next) {
  const prevByName = new Map((prev || []).map((s) => [s.name, s]));
  const nextByName = new Map((next || []).map((s) => [s.name, s]));
  const order = [];
  const seen = new Set();
  // Reihenfolge wie im neuen Stand, dann was nur im alten
  (next || []).forEach((s) => { if (!seen.has(s.name)) { order.push(s.name); seen.add(s.name); } });
  (prev || []).forEach((s) => { if (!seen.has(s.name)) { order.push(s.name); seen.add(s.name); } });

  const result = [];
  for (const name of order) {
    const p = prevByName.get(name);
    const n = nextByName.get(name);
    if (!p && n) { result.push({ name, status: "added" }); continue; }
    if (p && !n) { result.push({ name, status: "removed" }); continue; }
    const fields = [];
    ["headline", "body", "einleitung", "content"].forEach((f) => {
      const a = String(p[f] || "").trim();
      const b = String(n[f] || "").trim();
      if (a !== b) fields.push({ field: f, prev: a, next: b });
    });
    const pi = Array.isArray(p.items) ? p.items : [];
    const ni = Array.isArray(n.items) ? n.items : [];
    const sameItems = pi.length === ni.length && pi.every((it, idx) => it === ni[idx]);
    if (!sameItems) fields.push({ field: "items", prev: pi, next: ni });
    if (fields.length > 0) result.push({ name, status: "changed", fields });
  }
  return result;
}

function renderItemsDiff(prev, next) {
  const prevSet = new Set(prev);
  const nextSet = new Set(next);
  const added = next.filter((it) => !prevSet.has(it));
  const removed = prev.filter((it) => !nextSet.has(it));
  const reordered = added.length === 0 && removed.length === 0;
  return (
    <div className="diff-items">
      {reordered && <div className="diff-items-meta">Reihenfolge geändert ({prev.length} Einträge)</div>}
      {removed.map((it, i) => (
        <div key={"r" + i} className="diff-line diff-line--removed">− {truncate(it, 200)}</div>
      ))}
      {added.map((it, i) => (
        <div key={"a" + i} className="diff-line diff-line--added">+ {truncate(it, 200)}</div>
      ))}
    </div>
  );
}

function BackupsView({ anzeigenId, onBack }) {
  const [backups, setBackups] = useState([]);
  const [currentSections, setCurrentSections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allBackups, setAllBackups] = useState([]);
  const [expanded, setExpanded] = useState({});

  const loadBackups = useCallback(() => {
    if (!anzeigenId) return;
    return fetch(`/api/stellenanzeige/${anzeigenId}/backups`)
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data?.backups)) setBackups(data.backups);
        if (data?.current?.sections) setCurrentSections(data.current.sections);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [anzeigenId]);

  useEffect(() => {
    if (anzeigenId) {
      loadBackups();
    } else {
      fetch("/api/backups")
        .then((r) => r.json())
        .then((data) => { if (Array.isArray(data)) setAllBackups(data); setLoading(false); })
        .catch(() => setLoading(false));
    }
  }, [anzeigenId, loadBackups]);

  const handleRestore = async (backupId) => {
    if (!anzeigenId) return;
    if (!window.confirm("Diese Version wiederherstellen? Der aktuelle Stand wird als Sicherung abgelegt.")) return;
    try {
      await fetch(`/api/stellenanzeige/${anzeigenId}/backups/${backupId}/restore`, { method: "POST" });
      await loadBackups();
    } catch (err) {
      alert("Fehler beim Wiederherstellen: " + err.message);
    }
  };

  if (loading) {
    return <div className="loading-indicator"><div className="spinner" /><span>Lade Backups …</span></div>;
  }

  // All backups view (settings page)
  if (!anzeigenId) {
    return (
      <>
        <PageHeader
          eyebrow="Verwaltung"
          title="Backups"
          subtitle="Letzte Sicherungen aller Stellenanzeigen."
        />
        {allBackups.length === 0 ? (
          <div className="empty-card card">
            <HardDrive size={32} strokeWidth={1.5} />
            <p style={{ marginTop: 12 }}>Noch keine Backups vorhanden.</p>
          </div>
        ) : (
          <div className="archive-list">
            {allBackups.map((b) => (
              <div className="archive-row card" key={b.id} onClick={() => {
                navigate(`/jobs/backups/${b.stellenanzeige_id}`);
              }}>
                <div className="archive-info">
                  <h4>{b.stellentitel}</h4>
                  <span className="archive-meta">
                    {b.unternehmen} · Backup vom {formatDate(b.created_at)} · {new Date(b.created_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
                <ChevronRight size={16} />
              </div>
            ))}
          </div>
        )}
      </>
    );
  }

  // Verlauf als Diff-Liste: pro Backup berechnen wir die Änderung
  // gegen den nachfolgenden Stand (i=0: gegen aktuellen Stand).
  const sortedBackups = [...backups].sort((a, b) =>
    new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at)
  );
  const dayLabel = (d) => {
    const dt = new Date(d);
    const today = new Date();
    const ymd = (x) => `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`;
    if (ymd(dt) === ymd(today)) return "Heute";
    const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
    if (ymd(dt) === ymd(yesterday)) return "Gestern";
    return dt.toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });
  };

  const sessions = sortedBackups
    .map((b, i) => {
      const before = b.sections;
      const after = i === 0 ? (currentSections || b.sections) : sortedBackups[i - 1].sections;
      const ts = b.updated_at || b.created_at;
      return {
        id: b.id,
        ts,
        author: b.created_by_email || "System",
        diffs: diffSections(before, after),
      };
    })
    .filter((s) => s.diffs.length > 0);

  const days = [];
  let currentDayEntry = null;
  for (const s of sessions) {
    const label = dayLabel(s.ts);
    if (!currentDayEntry || currentDayEntry.label !== label) {
      currentDayEntry = { label, entries: [] };
      days.push(currentDayEntry);
    }
    currentDayEntry.entries.push(s);
  }

  return (
    <>
      <button className="btn-back" onClick={onBack}>Zurück</button>
      <PageHeader
        eyebrow="Verwaltung"
        title="Änderungsverlauf"
        subtitle="Nur die Änderungen je Bearbeitungs-Session, gruppiert nach Tag."
      />
      {backups.length === 0 ? (
        <div className="empty-card card">
          <HardDrive size={32} strokeWidth={1.5} />
          <p style={{ marginTop: 12 }}>Noch keine Änderungen. Sobald Du etwas bearbeitest, taucht es hier auf.</p>
        </div>
      ) : (
        <div className="history-list">
          {days.map((day) => (
            <div className="history-day" key={day.label}>
              <div className="history-day-label">{day.label}</div>
              {day.entries.map((s) => {
                const noChange = s.diffs.length === 0;
                const isOpen = expanded[s.id] !== false; // standardmäßig offen
                return (
                  <div className="history-entry card" key={s.id}>
                    <div className="history-entry-row">
                      <button
                        type="button"
                        className="history-entry-meta history-entry-toggle"
                        onClick={() => setExpanded((p) => ({ ...p, [s.id]: !isOpen }))}
                      >
                        <span className="history-toggle-chevron">{isOpen ? "▾" : "▸"}</span>
                        <span className="history-author-dot" />
                        <span className="history-author">{s.author}</span>
                        <span className="history-time">
                          {new Date(s.ts).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
                        </span>
                        <span className="history-change-summary">
                          {noChange ? "keine Änderungen" : `${s.diffs.length} Abschnitt${s.diffs.length > 1 ? "e" : ""} geändert`}
                        </span>
                      </button>
                      <button className="btn-secondary btn-sm" onClick={() => handleRestore(s.id)}>
                        <RotateCcw size={13} /> Auf diesen Stand zurück
                      </button>
                    </div>
                    {isOpen && !noChange && (
                      <div className="history-diffs">
                        {s.diffs.map((d, di) => (
                          <div key={di} className="diff-section">
                            <div className="diff-section-name">
                              {d.name}
                              {d.status === "added" && <span className="diff-badge diff-badge--added">Neu</span>}
                              {d.status === "removed" && <span className="diff-badge diff-badge--removed">Entfernt</span>}
                            </div>
                            {d.status === "changed" && d.fields.map((f, fi) => (
                              <div key={fi} className="diff-field">
                                <span className="diff-field-label">{FIELD_LABELS[f.field] || f.field}</span>
                                {f.field === "items"
                                  ? renderItemsDiff(f.prev, f.next)
                                  : (
                                    <>
                                      <div className="diff-line diff-line--removed">− {truncate(f.prev) || <em>(leer)</em>}</div>
                                      <div className="diff-line diff-line--added">+ {truncate(f.next) || <em>(leer)</em>}</div>
                                    </>
                                  )
                                }
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// --- URL helpers ---

function getSubPath() {
  const path = window.location.pathname.replace(/^\/+/, "");
  // Expected patterns: jobs, jobs/<partnerId>, jobs/<partnerId>/<anzeigenId>, jobs/papierkorb
  if (!path.startsWith("jobs")) return { type: "overview" };
  const parts = path.split("/").slice(1); // remove "jobs"
  if (parts.length === 0 || !parts[0]) return { type: "overview" };
  if (parts[0] === "papierkorb") return { type: "papierkorb" };
  if (parts[0] === "backups") return { type: "backups", anzeigenId: parts[1] || null };
  // Partner ID (numeric)
  const partnerId = decodeURIComponent(parts[0]);
  if (parts.length >= 2 && /^\d+$/.test(parts[1])) {
    return { type: "detail", partnerId, anzeigenId: parts[1] };
  }
  return { type: "archive", partnerId };
}

function pushJobsUrl(subPath) {
  const url = subPath ? `/jobs/${subPath}` : "/jobs";
  window.history.pushState(null, "", url);
}

// --- Main page ---

function UneqList({ onOpen }) {
  const [anzeigen, setAnzeigen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [nTitel, setNTitel] = useState("");
  const [nFarbtyp, setNFarbtyp] = useState("rot-gelb");
  const [nNotizen, setNNotizen] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("date-desc");

  const load = useCallback(() => {
    setLoading(true);
    fetch("/api/uneq/stellenanzeigen", { credentials: "include" })
      .then((r) => r.json())
      .then((d) => { setAnzeigen(Array.isArray(d.anzeigen) ? d.anzeigen : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!nTitel.trim()) return;
    setCreating(true);
    try {
      const res = await fetch("/api/uneq/stellenanzeigen", {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ stellentitel: nTitel.trim(), farbtyp: nFarbtyp, notizen: nNotizen.trim() }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Fehler");
      setShowCreate(false); setNTitel(""); setNNotizen("");
      onOpen(d.anzeige);
    } catch (err) { alert("Fehler: " + err.message); }
    finally { setCreating(false); }
  };

  const del = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm("Diese UNEQ-Anzeige in den Papierkorb verschieben?")) return;
    await fetch(`/api/uneq/stellenanzeigen/${id}`, { method: "DELETE", credentials: "include" });
    setAnzeigen((prev) => prev.filter((a) => a.id !== id));
  };

  const farbLabel = (v) => (FARBTYP_OPTIONS.find((o) => o.value === v) || {}).label || v;
  const q = search.trim().toLowerCase();
  const filtered = (q
    ? anzeigen.filter((a) => (a.stellentitel || "").toLowerCase().includes(q) || farbLabel(a.farbtyp).toLowerCase().includes(q))
    : anzeigen.slice()
  ).sort((a, b) => {
    if (sort === "name-asc") return (a.stellentitel || "").localeCompare(b.stellentitel || "", "de");
    if (sort === "name-desc") return (b.stellentitel || "").localeCompare(a.stellentitel || "", "de");
    const da = new Date(a.updated_at || a.created_at).getTime();
    const db = new Date(b.updated_at || b.created_at).getTime();
    return sort === "date-asc" ? da - db : db - da;
  });
  const adCost = (a) => (Number(a.cost_eur) > 0 ? formatEur(Number(a.cost_eur)) : formatCost(parseInt(a.total_tokens, 10) || 0));
  const totalCostEur = anzeigen.reduce((s, a) => s + (Number(a.cost_eur) || 0), 0);
  const totalTokens = anzeigen.reduce((s, a) => s + (parseInt(a.total_tokens, 10) || 0), 0);
  const totalCostLabel = totalCostEur > 0 ? formatEur(totalCostEur) : formatCost(totalTokens);

  return (
    <>
      <div className="uneq-list-bar">
        <div className="search-input-wrap toolbar-search">
          <Search size={16} />
          <input
            type="text"
            className="search-input"
            placeholder="UNEQ-Stelle suchen …"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && <button className="search-clear" onClick={() => setSearch("")}>&times;</button>}
        </div>
        <div className="toolbar-select">
          <ArrowUpDown size={14} />
          <select className="sort-select" value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sortierung">
            <option value="date-desc">Neueste zuerst</option>
            <option value="date-asc">Älteste zuerst</option>
            <option value="name-asc">Name A–Z</option>
            <option value="name-desc">Name Z–A</option>
          </select>
        </div>
        <button className="btn-primary" onClick={() => setShowCreate(true)}><Plus size={16} /> Neue UNEQ-Stelle</button>
      </div>

      {!loading && anzeigen.length > 0 && (
        <p className="page-counter">
          <strong>{anzeigen.length}</strong> UNEQ-Stelle{anzeigen.length !== 1 ? "n" : ""} · Gesamtkosten: <strong>{totalCostLabel}</strong>
        </p>
      )}

      {loading ? (
        <div className="loading-indicator"><div className="spinner" /><span>Lade …</span></div>
      ) : anzeigen.length === 0 ? (
        <div className="empty-card card">
          <FolderOpen size={32} strokeWidth={1.5} />
          <p style={{ marginTop: 12 }}>Noch keine UNEQ-Stellenanzeigen. Lege eine neue an.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-card card">
          <Search size={32} strokeWidth={1.5} />
          <p style={{ marginTop: 12 }}>Keine UNEQ-Stelle gefunden.</p>
        </div>
      ) : (
        <div className="cards-grid">
          {filtered.map((a) => (
            <div key={a.id} className="tk-card uneq-card" role="button" tabIndex={0}
              onClick={() => onOpen(a)} onKeyDown={(e) => { if (e.key === "Enter") onOpen(a); }}>
              <div className="uneq-card-head">
                <h3>{a.stellentitel}</h3>
                <button className="chat-session-del" onClick={(e) => del(e, a.id)} title="Löschen"><Trash2 size={14} /></button>
              </div>
              <div className="uneq-card-meta">
                <span className={"status-pill status-pill--" + (a.status || "entwurf") + " status-pill--readonly"}>{statusLabel(a.status)}</span>
                <span className="uneq-card-farb">{farbLabel(a.farbtyp)}</span>
              </div>
              <div className="uneq-card-foot">
                <span className="uneq-card-date">{formatDate(a.updated_at || a.created_at)}</span>
                <span className="uneq-card-cost"><Coins size={12} /> {adCost(a)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <div className="modal-overlay" onClick={(e) => { if (e.target.classList.contains("modal-overlay")) setShowCreate(false); }}>
          <div className="modal uneq-modal">
            <h3 className="uneq-modal-title">Neue UNEQ-Stelle</h3>
            <div className="chat-wizard-field">
              <label>Stellentitel</label>
              <input className="chat-input-line" value={nTitel} onChange={(e) => setNTitel(e.target.value)}
                placeholder="z.B. Videograf / Videocutter" autoFocus
                onKeyDown={(e) => { if (e.key === "Enter") create(); }} />
            </div>
            <div className="chat-wizard-field" style={{ marginTop: 14 }}>
              <label>Persönlichkeitstyp (Farbtyp)</label>
              <select className="chat-select" value={nFarbtyp} onChange={(e) => setNFarbtyp(e.target.value)}>
                {FARBTYP_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="chat-wizard-field" style={{ marginTop: 14 }}>
              <label>Notizen <span className="uneq-modal-optional">(optional)</span></label>
              <textarea className="chat-input-line uneq-modal-notes" rows={4}
                value={nNotizen} onChange={(e) => setNNotizen(e.target.value)}
                placeholder="Was steht schon fest? Stichpunkte, Rahmenbedingungen, Besonderheiten … (fließt in die Zielgruppen-Analyse ein)" />
            </div>
            <div className="rule-actions" style={{ marginTop: 20 }}>
              <button className="btn-primary" disabled={!nTitel.trim() || creating} onClick={create}>
                {creating ? "Lege an …" : "Anlegen & öffnen"}
              </button>
              <button className="btn-ai-cancel" onClick={() => setShowCreate(false)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function StellenanzeigenPage({ initialView }) {
  const [partners, setPartners] = useState([]);
  const [loadingPartners, setLoadingPartners] = useState(true);
  const [showNewProject, setShowNewProject] = useState(false);
  const [view, setView] = useState(() => initialView === "backups" ? { type: "backups", anzeigenId: null } : getSubPath());
  const [activeJobs, setActiveJobs] = useState([]);
  const [activeJobsLoaded, setActiveJobsLoaded] = useState(false);
  const [jotformCounts, setJotformCounts] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [partnerSort, setPartnerSort] = useState("date-desc");
  const [jobsMode, setJobsMode] = useState("partner"); // "partner" or "uneq"

  // Resolve partner/anzeige objects from URL IDs
  const [resolvedPartner, setResolvedPartner] = useState(null);
  const [resolvedAnzeige, setResolvedAnzeige] = useState(null);

  const setViewAndUrl = useCallback((newView, urlPath) => {
    setView(newView);
    if (urlPath !== undefined) pushJobsUrl(urlPath);
  }, []);

  const loadPartners = useCallback(() => {
    setLoadingPartners(true);
    fetch("/api/partners")
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data)) setPartners(data); setLoadingPartners(false); })
      .catch(() => setLoadingPartners(false));
  }, []);

  useEffect(() => { loadPartners(); }, [loadPartners]);

  // On mount: recover any active generation jobs from the backend
  useEffect(() => {
    if (activeJobsLoaded) return;
    fetch("/api/generation-jobs", { credentials: "include" })
      .then((r) => r.json())
      .then((jobs) => {
        if (Array.isArray(jobs) && jobs.length > 0) {
          setActiveJobs(jobs.filter((j) => j.status === "pending" || j.status === "running"));
        }
      })
      .catch(() => {})
      .finally(() => setActiveJobsLoaded(true));
  }, [activeJobsLoaded]);

  // Load Jotform counts
  useEffect(() => {
    fetch("/api/partners/jotform-counts")
      .then((r) => r.json())
      .then((data) => { if (data && typeof data === "object") setJotformCounts(data); })
      .catch(() => {});
  }, [partners]);

  // Listen for popstate (browser back/forward)
  useEffect(() => {
    const onPopState = () => setView(getSubPath());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  // Resolve partner from URL (by partner_id)
  useEffect(() => {
    if ((view.type === "archive" || view.type === "detail") && view.partnerId && partners.length > 0) {
      const found = partners.find((p) => p.partner_id === view.partnerId);
      setResolvedPartner(found || null);
    } else if (view.partner) {
      setResolvedPartner(view.partner);
    }
  }, [view, partners]);

  // Resolve anzeige from URL (fetch from API if needed)
  useEffect(() => {
    if (view.type === "detail" && view.anzeigenId && !view.anzeige) {
      const partner = view.fromArchive || resolvedPartner;
      if (!partner) return;
      fetch(`/api/partners/${encodeURIComponent(partner.partner_id)}/stellenanzeigen`)
        .then((r) => r.json())
        .then((data) => {
          if (Array.isArray(data)) {
            const found = data.find((a) => String(a.id) === String(view.anzeigenId));
            setResolvedAnzeige(found || null);
          }
        })
        .catch(() => setResolvedAnzeige(null));
    } else if (view.type === "detail" && view.anzeige) {
      setResolvedAnzeige(view.anzeige);
    }
  }, [view, resolvedPartner]);

  // Poll active generation jobs
  useEffect(() => {
    if (activeJobs.length === 0) return;
    const partnerIds = [...new Set(activeJobs.map((j) => j.partner_id))];
    const interval = setInterval(async () => {
      try {
        const allJobs = [];
        for (const pid of partnerIds) {
          const res = await fetch(`/api/generation-jobs?partner_id=${encodeURIComponent(pid)}`, { credentials: "include" });
          const jobs = await res.json();
          if (Array.isArray(jobs)) allJobs.push(...jobs);
        }
        const stillActive = allJobs.filter((j) => j.status === "pending" || j.status === "running");
        const justCompleted = activeJobs.some((prev) =>
          !stillActive.find((j) => j.id === prev.id)
        );
        setActiveJobs(stillActive);
        if (justCompleted) loadPartners();
      } catch (err) {
        console.error("Poll error:", err);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [activeJobs, loadPartners]);

  const handleStartGenerate = async (sub, partner) => {
    try {
      const opts = { method: "POST", credentials: "include" };
      const body = {};
      if (sub.farbtypOverride) body.farbtyp = sub.farbtypOverride;
      // Screener-Funde mitschicken, damit sie als persistentes Banner an der Anzeige landen.
      if (sub.screener && Array.isArray(sub.screener.warnings) && sub.screener.warnings.length > 0) {
        body.screener = sub.screener;
      }
      if (Object.keys(body).length > 0) {
        opts.headers = { "Content-Type": "application/json" };
        opts.body = JSON.stringify(body);
      }
      const res = await fetch(`/api/jotform/submissions/${sub.submissionId}/generate-async`, opts);
      const data = await res.json();
      if (data.jobId) {
        // Use partnerId from backend response (numeric) to ensure polling matches
        const resolvedPartnerId = data.partnerId || partner.partner_id;
        setActiveJobs((prev) => [...prev, {
          id: data.jobId,
          partner_id: resolvedPartnerId,
          stellentitel: sub.stellentitel || "Wird generiert...",
          status: "pending",
          stage: "agent1",
        }]);
      }
    } catch (err) {
      console.error("Start generate error:", err);
    }
  };

  const handleRegenerate = async (anzeige, partner) => {
    const res = await fetch(`/api/stellenanzeigen/${anzeige.id}/regenerate-async`, {
      method: "POST",
      credentials: "include",
    });
    const data = await res.json();
    if (data.jobId) {
      const resolvedPartnerId = data.partnerId || partner.partner_id;
      setActiveJobs((prev) => [...prev, {
        id: data.jobId,
        partner_id: resolvedPartnerId,
        stellentitel: anzeige.stellentitel || "Wird neu generiert...",
        status: "pending",
        stage: "agent1",
      }]);
    } else {
      throw new Error(data.error || "Regenerate fehlgeschlagen");
    }
  };

  // Filter + sort partners (must be before conditional returns - hooks rules)
  const activePartners = useMemo(() => {
    const filtered = partners.filter((p) => {
      const isArchived = p.archived === true || p.archived === "true";
      if (!showArchived && isArchived) return false;
      if (showArchived && !isArchived) return false;
      if (searchQuery.trim()) {
        return p.unternehmen.toLowerCase().includes(searchQuery.toLowerCase());
      }
      return true;
    });
    switch (partnerSort) {
      case "name-asc": filtered.sort((a, b) => a.unternehmen.localeCompare(b.unternehmen, "de")); break;
      case "name-desc": filtered.sort((a, b) => b.unternehmen.localeCompare(a.unternehmen, "de")); break;
      case "date-asc": filtered.sort((a, b) => new Date(a.last_generated) - new Date(b.last_generated)); break;
      case "date-desc":
      default: filtered.sort((a, b) => new Date(b.last_generated) - new Date(a.last_generated)); break;
    }
    return filtered;
  }, [partners, searchQuery, showArchived, partnerSort]);

  // Backups view
  if (view.type === "backups") {
    return (
      <BackupsView
        anzeigenId={view.anzeigenId}
        onBack={() => {
          window.history.back();
        }}
      />
    );
  }

  // Papierkorb view
  if (view.type === "papierkorb") {
    return (
      <Papierkorb onBack={() => { setViewAndUrl({ type: "overview" }, ""); loadPartners(); }} />
    );
  }

  // Detail view
  if (view.type === "detail") {
    const anzeige = view.anzeige || resolvedAnzeige;
    const partner = view.fromUneq ? null : (view.fromArchive || resolvedPartner);
    if (!anzeige) {
      return <div className="loading-indicator"><div className="spinner" /><span>Lade Stellenanzeige …</span></div>;
    }
    return (
      <StellenDetail
        anzeige={anzeige}
        onBack={() => {
          if (partner) {
            setViewAndUrl({ type: "archive", partner }, partner.partner_id);
          } else {
            setViewAndUrl({ type: "overview" }, "");
            loadPartners();
          }
        }}
      />
    );
  }

  // Archive view
  if (view.type === "archive") {
    const partner = view.partner || resolvedPartner;
    if (!partner) {
      if (loadingPartners) {
        return <div className="loading-indicator"><div className="spinner" /><span>Lade Partner …</span></div>;
      }
      return (
        <>
          <button className="btn-back" onClick={() => { setViewAndUrl({ type: "overview" }, ""); }}>Zurück zur Übersicht</button>
          <p>Partner nicht gefunden.</p>
        </>
      );
    }
    return (
      <PartnerArchive
        partner={partner}
        onBack={() => { setViewAndUrl({ type: "overview" }, ""); loadPartners(); }}
        onSelectAnzeige={(a) => setViewAndUrl(
          { type: "detail", anzeige: a, fromArchive: partner },
          `${partner.partner_id}/${a.id}`
        )}
        activeJobs={activeJobs}
        onStartGenerate={handleStartGenerate}
        onRegenerate={handleRegenerate}
      />
    );
  }

  const totalCount = activePartners.reduce((sum, p) => sum + parseInt(p.count, 10), 0);
  const totalTokens = activePartners.reduce((sum, p) => sum + parseInt(p.total_tokens || 0, 10), 0);
  const totalCostEur = activePartners.reduce((sum, p) => sum + (Number(p.cost_eur) || 0), 0);
  const archivedCount = partners.filter((p) => p.archived === true || p.archived === "true").length;

  // Check which partners have active jobs
  const partnerJobCounts = {};
  for (const j of activeJobs) {
    partnerJobCounts[j.partner_id] = (partnerJobCounts[j.partner_id] || 0) + 1;
  }

  const handleArchivePartner = async (e, partner) => {
    e.stopPropagation();
    const isArchived = partner.archived === true || partner.archived === "true";
    const action = isArchived ? "wiederherstellen" : "abschließen";
    if (!window.confirm(`"${partner.unternehmen}" wirklich ${action}?`)) return;
    try {
      await fetch(`/api/partners/${encodeURIComponent(partner.partner_id)}/archive`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ archived: !isArchived }),
      });
      loadPartners();
    } catch (err) {
      console.error("Archive error:", err);
    }
  };

  // Overview
  return (
    <>
      <PageHeader
        eyebrow="Texte"
        title="Stellenanzeigen"
        subtitle="Übersicht aller Partner und ihrer generierten Stellenanzeigen."
        actions={
          <>
            <div className="view-toggle">
              <button className={`toggle-btn ${jobsMode === "partner" ? "active" : ""}`} onClick={() => setJobsMode("partner")}>Partner</button>
              <button className={`toggle-btn ${jobsMode === "uneq" ? "active" : ""}`} onClick={() => setJobsMode("uneq")}>UNEQ</button>
            </div>
            <button
              className="icon-btn"
              onClick={() => setViewAndUrl({ type: "papierkorb" }, "papierkorb")}
              data-tooltip="Papierkorb"
              aria-label="Papierkorb"
            >
              <Trash2 size={16} />
            </button>
            {jobsMode === "partner" && (
              <button className="btn-primary" onClick={() => setShowNewProject(true)}>
                <Plus size={16} />
                Neues Projekt
              </button>
            )}
          </>
        }
      />

      {jobsMode === "uneq" ? (
        <UneqList onOpen={(a) => setView({ type: "detail", anzeige: a, fromUneq: true })} />
      ) : (
      <>
      {/* Search + filter + sort bar */}
      {!loadingPartners && partners.length > 0 && (
        <div className="toolbar">
          <div className="search-input-wrap toolbar-search">
            <Search size={16} />
            <input
              type="text"
              placeholder="Partner suchen …"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button className="search-clear" onClick={() => setSearchQuery("")}>&times;</button>
            )}
          </div>

          <div className="toolbar-select">
            <ArrowUpDown size={14} />
            <select
              className="sort-select"
              value={partnerSort}
              onChange={(e) => setPartnerSort(e.target.value)}
              aria-label="Sortierung"
            >
              <option value="date-desc">Neueste zuerst</option>
              <option value="date-asc">Älteste zuerst</option>
              <option value="name-asc">Name A–Z</option>
              <option value="name-desc">Name Z–A</option>
            </select>
          </div>

          {archivedCount > 0 && (
            <button
              className={`btn-secondary ${showArchived ? "active" : ""}`}
              onClick={() => setShowArchived(!showArchived)}
            >
              <Archive size={14} />
              Abgeschlossene ({archivedCount})
            </button>
          )}
        </div>
      )}

      {!loadingPartners && activePartners.length > 0 && (
        <p className="page-counter">
          <strong>{activePartners.length}</strong> Partner · <strong>{totalCount}</strong> Stelle{totalCount !== 1 ? "n" : ""} · Gesamtkosten: <strong>{totalCostEur > 0 ? formatEur(totalCostEur) : formatCost(totalTokens)}</strong>
        </p>
      )}

      {loadingPartners ? (
        <div className="loading-indicator"><div className="spinner" /><span>Lade Partner …</span></div>
      ) : activePartners.length === 0 ? (
        <div className="empty-card card">
          <FolderOpen size={32} strokeWidth={1.5} />
          <p style={{ marginTop: 12 }}>
            {searchQuery ? "Keine Partner gefunden." : showArchived ? "Keine abgeschlossenen Partner." : "Noch keine Projekte vorhanden. Lege ein neues Projekt an."}
          </p>
        </div>
      ) : (
        <div className="cards-grid">
          {activePartners.map((p) => {
            const jfCount = jotformCounts[p.partner_id] || 0;
            const genCount = parseInt(p.generated_count, 10) || 0;
            const isArchived = p.archived === true || p.archived === "true";
            const isGenerating = !!partnerJobCounts[p.partner_id];
            return (
              <div
                key={p.partner_id}
                className={`tk-card ${isArchived ? "tk-card--archived" : ""}`}
                onClick={() => setViewAndUrl({ type: "archive", partner: p }, p.partner_id)}
                role="button"
                tabIndex={0}
              >
                {isGenerating && (
                  <span className="tk-card-badge" title="Generierung läuft">
                    <Loader size={10} className="spinning" />
                    Aktiv
                  </span>
                )}

                <div className="tk-card-head">
                  <div className="tk-card-avatar">{(p.unternehmen || "?")[0].toUpperCase()}</div>
                  <div className="tk-card-headtext">
                    <div className="tk-card-name">
                      {p.unternehmen}
                      {isArchived && <span className="tk-card-tag">Abgeschlossen</span>}
                    </div>
                  </div>
                </div>

                <div className="tk-card-stats">
                  <div className="tk-stat">
                    <div className="tk-stat-label">Einreichungen</div>
                    <div className="tk-stat-value">{jfCount}</div>
                  </div>
                  <div className="tk-stat">
                    <div className="tk-stat-label">Generiert</div>
                    <div className="tk-stat-value">{genCount}</div>
                  </div>
                  <div className="tk-stat">
                    <div className="tk-stat-label">Kosten</div>
                    <div className="tk-stat-value">{p.cost_eur != null ? formatEur(p.cost_eur) : formatCost(p.total_tokens)}</div>
                  </div>
                  <div className="tk-stat">
                    <div className="tk-stat-label">Letzte Anzeige</div>
                    <div className="tk-stat-value tk-stat-value--soft">{formatDate(p.last_generated)}</div>
                  </div>
                </div>

                <div className="tk-card-foot">
                  <span className="tk-card-foot-label">
                    {genCount > 0
                      ? `${genCount} Anzeige${genCount !== 1 ? "n" : ""} im Archiv`
                      : "Noch keine Anzeigen"}
                  </span>
                  <button
                    className={`tk-card-icon-btn ${isArchived ? "tk-card-icon-btn--restore" : ""}`}
                    onClick={(e) => handleArchivePartner(e, p)}
                    data-tooltip={isArchived ? "Wiederherstellen" : "Abschließen"}
                  >
                    {isArchived ? <ArchiveRestore size={14} /> : <Archive size={14} />}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showNewProject && (
        <NewProjectModal
          onClose={() => setShowNewProject(false)}
          onCreate={(newPartner) => {
            setShowNewProject(false);
            loadPartners();
            setViewAndUrl(
              { type: "archive", partner: newPartner },
              newPartner.partner_id
            );
          }}
        />
      )}
      </>
      )}
    </>
  );
}
